import type {
    DocumentType,
    Invoice,
    InvoiceDatevConfig,
    InvoiceLine,
    InvoiceStatus,
    TaxGroup,
} from '../../types';
import { isExternalAdvanceId } from '../externalAdvances';
import {
    CONTRIBUTES_TO_NET,
    computeLineNet,
    effectiveLineDiscountPercent,
    performancePeriod,
} from '../invoiceLogic';
import { roundCents } from '../money';
import { documentGuidForInvoice } from './belegArchive';
import { toCp1252Bytes } from './cp1252';

/**
 * DATEV-Export im Format EXTF Buchungsstapel, Formatversion 700.
 *
 * Ein Exporter fuer beide Kanzlei-Systeme: Simba liest das DATEV-Format ein,
 * ein zweiter Exporter waere doppelte Arbeit und eine zweite Fehlerquelle.
 *
 * Gebucht wird je STEUERGRUPPE UND LEISTUNGSMONAT, nicht je Rechnung.
 *
 * Je Steuergruppe, weil das Erloeskonto den Steuersatz traegt — eine
 * Sammelzeile liesse sich in der Buchhaltung nicht mehr in die Steuersaetze
 * aufteilen. Je Monat, weil der Betrieb Teilleistungen ueber Zeitraeume
 * abrechnet: ein Beleg ueber Juni bis August gehoert in drei Perioden, und
 * eine Zeile mit einem Zeitraum daneben zwingt die Kanzlei, die Abgrenzung von
 * Hand nachzubauen. Eine Rechnung mit 19 % und 7 % ueber drei Monate erzeugt
 * damit bis zu sechs Zeilen; mehrere Zeilen unter derselben Belegnummer sind
 * in DATEV der Normalfall.
 *
 * ════════════════════════════════════════════════════════════════════════
 *  GRUNDLAGE: ein echter, von der Kanzlei akzeptierter EXTF-Export
 * ════════════════════════════════════════════════════════════════════════
 *  Das Format ist nicht mehr aus dem Gedaechtnis rekonstruiert. Referenz ist
 *  ein von sevDesk erzeugter und von der Kanzlei eingelesener Stapel:
 *
 *    Downloads/BITTE-ENTPACKEN_Buchungsstapel_und_Belegbilder_245_514_
 *    20260101_bis_20260731/EXTF_Buchungsstapel_20260101_bis_20260731.csv
 *    (CP1252; Berater 245 / Mandant 514; "EXTF";700;21;"Buchungsstapel";13)
 *
 *  Dagegen abgeglichen und damit belegt sind: die 31 Felder der Kopfzeile in
 *  ihrer Reihenfolge, die 125 Spalten der Ueberschriftszeile in Reihenfolge
 *  und Schreibweise, Belegdatum als TTMM, Leistungsdatum als TTMMJJJJ,
 *  Belegfeld 2 als TTMMJJ, der Beleglink als BEDI "<guid>" sowie das
 *  Betragsformat mit Dezimalkomma und zwei Nachkommastellen.
 *
 *  Ebenfalls aus der Referenz uebernommen ist die Belegung der acht
 *  Beleginfo-Paare (Spalten 21-36) — dieselbe Angabe im selben Paar, weil die
 *  Kanzlei sie dort sucht, wo sie sie kennt: 2 Umsatzsteuerprozent, 3 Name,
 *  5 Nettobetrag, 6 Steuerbetrag, 7 Leistungsdatum (dort auch als Zeitraum
 *  "TTMMJJJJ - TTMMJJJJ"), 8 Kundennummer. Paar 1 bleibt leer.
 *
 *  Paar 4 laesst die Referenz frei, und genau deshalb steht dort unsere
 *  einzige eigene Angabe: der Leistungsmonat der Zeile mit seinem Netto
 *  ("06/2026: 780,00"). Die Kanzlei hat genau danach gefragt; belegt wird
 *  dafuer nur, was in der Referenz ungenutzt ist, damit die Slot-Treue der
 *  uebrigen sieben Paare erhalten bleibt.
 *
 *  Der Abgleich hat einen Fehler aufgedeckt, den ohne Referenz niemand
 *  finden konnte: unsere Liste hatte 123 statt 125 Spalten — es fehlten
 *  "BU 49 Hauptfunktionsnummer" (46) und "Abw. Skontokonto" (125). DATEV
 *  liest nach POSITION, nicht nach Spaltenname; ab Spalte 46 lag deshalb
 *  alles eine Stelle zu weit links. Die Festschreibung landete im
 *  Kennzeichen SoBil-Buchung, das Leistungsdatum in der Festschreibung: der
 *  Stapel kam unfestgeschrieben (nicht GoBD-konform) und ohne
 *  Leistungsdatum in der Kanzlei an.
 *
 *  Befuellt wird deshalb weiterhin ueber den SPALTENNAMEN und nie ueber
 *  einen Index — ein falscher Name scheitert laut. Zusaetzlich prueft der
 *  Test die POSITION auf der fertig gerenderten Zeile gegen harte Zahlen;
 *  ein Test, der die Spaltenliste nur mit sich selbst vergleicht, haette
 *  genau diesen Fehler nicht gefunden.
 *
 *  Die verbliebenen offenen Punkte stehen am Dateiende in
 *  UNVERIFIED_DATEV_FIELDS und sind fuer eine Warnung in der Oberflaeche
 *  gedacht. Ein Grep nach "PRUEFEN:" findet dieselben Stellen im Code.
 * ════════════════════════════════════════════════════════════════════════
 */

// ═══════════════════════════════════════════════════════════════════════════
// Formatzeilen aus der Referenzdatei — hier austauschen, sonst nirgends
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die 31 Felder der EXTF-Kopfzeile (Zeile 1), gegen die Referenzdatei
 * abgeglichen: sie fuehrt dieselben 31 Felder in derselben Reihenfolge.
 *
 * Die Namen dienen nur der Lesbarkeit und der Fehlersuche; massgeblich ist die
 * REIHENFOLGE. Wer die Konstante ersetzt, muss die Fuellfunktion
 * buildHeaderRow einmal mitlesen — sie referenziert die Felder ueber genau
 * diese Namen.
 */
export const EXTF_HEADER_FIELD_NAMES: readonly string[] = Object.freeze([
    'Kennzeichen', //                1
    'Versionsnummer', //             2
    'Formatkategorie', //            3
    'Formatname', //                 4
    'Formatversion', //              5
    'Erzeugt am', //                 6
    'Importiert', //                 7  (reserviert, bleibt leer)
    'Herkunft', //                   8
    'Exportiert von', //             9
    'Importiert von', //            10  (reserviert, bleibt leer)
    'Berater', //                   11
    'Mandant', //                   12
    'WJ-Beginn', //                 13
    'Sachkontenlänge', //           14
    'Datum von', //                 15
    'Datum bis', //                 16
    'Bezeichnung', //               17
    'Diktatkürzel', //              18
    'Buchungstyp', //               19
    'Rechnungslegungszweck', //     20
    'Festschreibung', //            21
    'WKZ', //                       22
    'reserviert 23', //             23
    'Derivatskennzeichen', //       24
    'reserviert 25', //             25
    'reserviert 26', //             26
    'SKR', //                       27
    'Branchenlösungs-Id', //        28
    'reserviert 29', //             29
    'reserviert 30', //             30
    'Anwendungsinformation', //     31
]);

/**
 * Die Spaltenueberschriftszeile (Zeile 2) des Buchungsstapels, Formatversion
 * 13 im Rahmenformat 700 — 1:1 aus der Referenzdatei uebernommen, 125 Spalten.
 *
 * Die Positionsnummern im Kommentar sind der schnellste Weg fuer den naechsten
 * Abgleich. Wer hier etwas einfuegt oder entfernt, verschiebt jede folgende
 * Spalte, und DATEV liest nach Position.
 *
 * Einzige bewusste Abweichung von der Referenz: sevDesk schreibt in
 * "Zusatzinformation – Art N" statt des Halbgeviertstrichs ein literales
 * Fragezeichen — ein Kodierungsschaden auf deren Seite. Hier steht der
 * korrekte Strich; Zeile 2 wertet DATEV ohnehin nicht aus.
 */
export const BUCHUNGSSTAPEL_COLUMNS_V13: readonly string[] = Object.freeze([
    'Umsatz', //    1
    'Soll-/Haben-Kennzeichen', //    2
    'WKZ Umsatz', //    3
    'Kurs', //    4
    'Basisumsatz', //    5
    'WKZ Basisumsatz', //    6
    'Konto', //    7
    'Gegenkonto (ohne BU-Schlüssel)', //    8
    'BU-Schlüssel', //    9
    'Belegdatum', //   10
    'Belegfeld 1', //   11
    'Belegfeld 2', //   12
    'Skonto', //   13
    'Buchungstext', //   14
    'Postensperre', //   15
    'Diverse Adressnummer', //   16
    'Geschäftspartnerbank', //   17
    'Sachverhalt', //   18
    'Zinssperre', //   19
    'Beleglink', //   20
    'Beleginfo-Art 1', //   21
    'Beleginfo-Inhalt 1', //   22
    'Beleginfo-Art 2', //   23
    'Beleginfo-Inhalt 2', //   24
    'Beleginfo-Art 3', //   25
    'Beleginfo-Inhalt 3', //   26
    'Beleginfo-Art 4', //   27
    'Beleginfo-Inhalt 4', //   28
    'Beleginfo-Art 5', //   29
    'Beleginfo-Inhalt 5', //   30
    'Beleginfo-Art 6', //   31
    'Beleginfo-Inhalt 6', //   32
    'Beleginfo-Art 7', //   33
    'Beleginfo-Inhalt 7', //   34
    'Beleginfo-Art 8', //   35
    'Beleginfo-Inhalt 8', //   36
    'KOST1-Kostenstelle', //   37
    'KOST2-Kostenstelle', //   38
    'KOST-Menge', //   39
    'EU-Mitgliedstaat u. UStID (Bestimmung)', //   40
    'EU-Steuersatz (Bestimmung)', //   41
    'Abw. Versteuerungsart', //   42
    'Sachverhalt L+L', //   43
    'Funktionsergänzung L+L', //   44
    'BU 49 Hauptfunktiontyp', //   45
    'BU 49 Hauptfunktionsnummer', //   46
    'BU 49 Funktionsergänzung', //   47
    'Zusatzinformation – Art 1', //   48
    'Zusatzinformation – Inhalt 1', //   49
    'Zusatzinformation – Art 2', //   50
    'Zusatzinformation – Inhalt 2', //   51
    'Zusatzinformation – Art 3', //   52
    'Zusatzinformation – Inhalt 3', //   53
    'Zusatzinformation – Art 4', //   54
    'Zusatzinformation – Inhalt 4', //   55
    'Zusatzinformation – Art 5', //   56
    'Zusatzinformation – Inhalt 5', //   57
    'Zusatzinformation – Art 6', //   58
    'Zusatzinformation – Inhalt 6', //   59
    'Zusatzinformation – Art 7', //   60
    'Zusatzinformation – Inhalt 7', //   61
    'Zusatzinformation – Art 8', //   62
    'Zusatzinformation – Inhalt 8', //   63
    'Zusatzinformation – Art 9', //   64
    'Zusatzinformation – Inhalt 9', //   65
    'Zusatzinformation – Art 10', //   66
    'Zusatzinformation – Inhalt 10', //   67
    'Zusatzinformation – Art 11', //   68
    'Zusatzinformation – Inhalt 11', //   69
    'Zusatzinformation – Art 12', //   70
    'Zusatzinformation – Inhalt 12', //   71
    'Zusatzinformation – Art 13', //   72
    'Zusatzinformation – Inhalt 13', //   73
    'Zusatzinformation – Art 14', //   74
    'Zusatzinformation – Inhalt 14', //   75
    'Zusatzinformation – Art 15', //   76
    'Zusatzinformation – Inhalt 15', //   77
    'Zusatzinformation – Art 16', //   78
    'Zusatzinformation – Inhalt 16', //   79
    'Zusatzinformation – Art 17', //   80
    'Zusatzinformation – Inhalt 17', //   81
    'Zusatzinformation – Art 18', //   82
    'Zusatzinformation – Inhalt 18', //   83
    'Zusatzinformation – Art 19', //   84
    'Zusatzinformation – Inhalt 19', //   85
    'Zusatzinformation – Art 20', //   86
    'Zusatzinformation – Inhalt 20', //   87
    'Stück', //   88
    'Gewicht', //   89
    'Zahlweise', //   90
    'Forderungsart', //   91
    'Veranlagungsjahr', //   92
    'Zugeordnete Fälligkeit', //   93
    'Skontotyp', //   94
    'Auftragsnummer', //   95
    'Buchungstyp', //   96
    'USt-Schlüssel (Anzahlungen)', //   97
    'EU-Mitgliedstaat (Anzahlungen)', //   98
    'Sachverhalt L+L (Anzahlungen)', //   99
    'EU-Steuersatz (Anzahlungen)', //  100
    'Erlöskonto (Anzahlungen)', //  101
    'Herkunft-Kz', //  102
    'Leerfeld', //  103
    'KOST-Datum', //  104
    'SEPA-Mandatsreferenz', //  105
    'Skontosperre', //  106
    'Gesellschaftername', //  107
    'Beteiligtennummer', //  108
    'Identifikationsnummer', //  109
    'Zeichnernummer', //  110
    'Postensperre bis', //  111
    'Bezeichnung SoBil-Sachverhalt', //  112
    'Kennzeichen SoBil-Buchung', //  113
    'Festschreibung', //  114
    'Leistungsdatum', //  115
    'Datum Zuord. Steuerperiode', //  116
    'Fälligkeit', //  117
    'Generalumkehr', //  118
    'Steuersatz', //  119
    'Land', //  120
    'Abrechnungsreferenz', //  121
    'BVV-Position (Betriebsvermögensvergleich)', //  122
    'EU-Mitgliedstaat u. UStID (Ursprung)', //  123
    'EU-Steuersatz (Ursprung)', //  124
    'Abw. Skontokonto', //  125
]);

// ═══════════════════════════════════════════════════════════════════════════
// Feste Formatwerte
// ═══════════════════════════════════════════════════════════════════════════

/** DATEV-Kennzeichen fuer einen extern erzeugten Stapel (DTVF waere DATEV-eigen). */
const EXTF_MARKER = 'EXTF';
/** Rahmen-Formatversion, hier fest 700. */
const EXTF_VERSION = 700;
/** Formatkategorie 21 = Buchungsstapel (Referenz: "EXTF";700;21;...). */
const FORMAT_CATEGORY_BUCHUNGSSTAPEL = 21;
const FORMAT_NAME_BUCHUNGSSTAPEL = 'Buchungsstapel';
/** Formatversion des Buchungsstapels im Rahmenformat 700 (Referenz: ...;13). */
const FORMAT_VERSION_BUCHUNGSSTAPEL = 13;
/** PRUEFEN: Buchungstyp 1 = Finanzbuchfuehrung. */
const BOOKING_TYPE_FIBU = 1;
/** Herkunftskennzeichen, laut Format genau zwei Zeichen. */
const ORIGIN_MARKER = 'GA';
export const CURRENCY_EUR = 'EUR';

/** Feldtrenner und Zeilenende — DATEV erwartet Semikolon und CRLF. */
export const FIELD_SEPARATOR = ';';
export const LINE_BREAK = '\r\n';

/**
 * PRUEFEN: Maximallaengen der Textfelder. Zu lange Werte werden gekuerzt, nicht
 * abgewiesen — ein abgeschnittener Buchungstext ist harmlos, ein abgebrochener
 * Export waere es nicht.
 */
const MAX_LENGTH_BELEGFELD_1 = 36;
export const MAX_LENGTH_BUCHUNGSTEXT = 60;
const MAX_LENGTH_BEZEICHNUNG = 30;

/**
 * PRUEFEN: Laenge von "Beleginfo-Inhalt". Die Referenz schoepft das Feld nicht
 * aus — ihr laengster Inhalt ist ein Kundenname mit 46 Zeichen —, und eine
 * belegbare Grenze aus der Formatbeschreibung liegt hier nicht vor. Deshalb
 * konservativ dieselbe Grenze wie beim Buchungstext: sie traegt jeden
 * Firmennamen aus dem Bestand und jede Zeitraumangabe, und was darueber
 * hinausgeht, ist gekuerzt harmlos — der volle Name steht im Debitorenstamm.
 *
 * Die Monatsangabe in Paar 4 nennt seit der Aufteilung in Monatszeilen nur noch
 * den EINEN Monat ihrer Zeile ("06/2026: 780,00") und kommt damit nie in die
 * Naehe der Grenze. Vorher stand dort die Aufstellung des ganzen Belegs, und
 * eine mitten im Betrag gekappte Geldliste sieht vollstaendig aus, ohne es zu
 * sein — die Aufteilung nimmt diesem Feld genau dieses Risiko.
 */
const MAX_LENGTH_BELEGINFO_INHALT = 60;

/**
 * Belege ab ISSUED duerfen exportiert werden; alles davor nicht.
 *
 * Entwuerfe haben noch keine Rechnungsnummer und sind jederzeit aenderbar —
 * eine Buchung darauf waere nicht nachvollziehbar. Sie werden hart abgewiesen
 * statt still uebersprungen: ein stiller Sprung faellt in der Buchhaltung erst
 * beim Kontenabgleich auf, und dann sucht jemand lange.
 */
const PRE_ISSUE_STATUSES: readonly InvoiceStatus[] = Object.freeze([
    'DRAFT',
    'READY_FOR_REVIEW',
    'APPROVED',
    'ISSUING',
]);

/**
 * Belegarten, die eine ERHALTENE ANZAHLUNG sind und keinen Erloes.
 *
 * Spiegelt ADVANCE_TYPES aus advanceInvoiceLogic.ts. Nachgezogen statt
 * importiert, weil die Konstante dort nicht exportiert ist und die Datei ein
 * Hotspot ist, den dieser Export nicht anfassen soll; die einzige Belegart
 * darin ist seit jeher ADVANCE_INVOICE. Wer sie dort erweitert, muss hier
 * mitziehen — ein Abschlag, der hier als Erloes durchgeht, bucht den Umsatz
 * ein zweites Mal.
 *
 * Bewusst OHNE Statusbedingung, anders als isDeductibleAdvance: fuer die Frage
 * "wie wurde dieser Beleg gebucht" ist allein die Belegart massgeblich. Ein
 * durch die Schlussrechnung abgeloester Abschlag (SUPERSEDED_BY_FINAL_INVOICE)
 * steht weiterhin auf dem Anzahlungskonto und muss genau deshalb dort
 * aufgeloest werden.
 */
const ADVANCE_DOCUMENT_TYPES: ReadonlySet<DocumentType> = new Set<DocumentType>([
    'ADVANCE_INVOICE',
]);

/**
 * Die Buchungsseite wird NICHT aus der Belegart abgeleitet.
 *
 * Es gab hier einmal eine Liste "Belegarten, die im Haben gebucht werden", die
 * mit dem Vorzeichen des Betrags verknuepft wurde. Das war falsch: Storno und
 * Gutschriften werden im gesamten Modul mit negativen Mengen erfasst und
 * tragen ihr Vorzeichen bereits selbst. Die doppelte Verneinung buchte sie auf
 * dieselbe Seite wie die Ursprungsrechnung und verdoppelte den Umsatz, statt
 * ihn aufzuheben.
 *
 * Die Seite folgt deshalb allein dem Vorzeichen — siehe buildBookingRow.
 * Die fachliche Frage, ob eine Gutschrift im umsatzsteuerlichen Sinn
 * (SELF_BILLING_CREDIT_NOTE) ueberhaupt negativ gefuehrt werden sollte, steht
 * weiterhin auf der Klaerungsliste mit der Kanzlei — sie ist eine Abrechnung
 * ueber UNSERE Leistung und damit fachlich ein Umsatz.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Hilfsfunktionen
// ═══════════════════════════════════════════════════════════════════════════
//
// Ein Teil davon ist EXPORTIERT, obwohl er hier privat entstanden ist:
// src/services/datev/creditNoteDatev.ts baut den Buchungsstapel der
// Freelancer-Gutschriften (KREDITORISCH, Phase D5) und braucht dieselbe
// Kopfzeile, dieselbe Feldbereinigung und dasselbe Setzen ueber den
// Spaltennamen. Nachgebaut waeren es zwei Formatfassungen, die auseinander
// driften — und ein Formatfehler faellt erst beim Steuerberater auf.
//
// Bewusst NUR exportiert, NICHT umgebaut: dieser Exporter bleibt in Aufbau,
// Reihenfolge und Verhalten unveraendert, alle Bestandstests gelten
// unveraendert weiter.

export interface DatevExportPeriod {
    /** yyyy-mm-dd */
    from: string;
    /** yyyy-mm-dd */
    to: string;
}

export interface DatevExportOptions {
    /** Erzeugungszeitpunkt der Kopfzeile. Injizierbar, damit Tests fest sind. */
    createdAt?: Date;
    /** Kuerzel oder Name des ausloesenden Benutzers. */
    exportedBy?: string;
    /** Freitext der Stapelbezeichnung; sonst wird sie aus dem Zeitraum gebaut. */
    description?: string;
    /**
     * Die Belege, deren PDF mit AUSGELIEFERT wird — nur sie bekommen einen
     * Beleglink. Uebergeben wird die Menge der Rechnungs-ids.
     *
     * Der Beleglink ist ein Verweis auf ein Dokument im Belegarchiv. Ihn zu
     * setzen, ohne das Dokument mitzuliefern, waere eine Behauptung ueber
     * etwas, das im Paket nicht liegt: der reine CSV-Export verschickt gar
     * keine Belege, und im ZIP fehlen genau die, die dem Nutzer als
     * "kein PDF im Archiv" gemeldet werden.
     *
     * Fehlt die Menge, bleibt der Beleglink also leer — das ist die richtige
     * Vorgabe fuer ein Paket ohne Belege. Wer Links will, exportiert das ZIP;
     * dort wird die CSV aus derselben Menge gebaut wie das Manifest.
     */
    belegLinkIds?: ReadonlySet<string>;

    /**
     * Belegart und Exportvermerk je ABGEZOGENEM Beleg, geschluesselt nach
     * AdvanceDeduction.advanceInvoiceId.
     *
     * Beides zusammen entscheidet, wie der Abzug gebucht wird, und das haengt
     * allein daran, wie der abgezogene Beleg SELBST gebucht wurde:
     *  - Abschlagsrechnung (ADVANCE_DOCUMENT_TYPES), die auf dem
     *    Anzahlungskonto liegt: keine Erlosbuchung, sondern eine erhaltene
     *    Anzahlung. Sie wird gegen das Anzahlungskonto aufgeloest.
     *  - Abschlagsrechnung, die VOR der Einrichtung des Anzahlungskontos
     *    exportiert wurde (exportedAt vor advancePaymentAccountSince): dieser
     *    Export selbst hat sie damals auf das ERLOESKONTO gebucht. Ihr Abzug
     *    mindert deshalb die Erloeszeile; eine Aufloesung gegen das
     *    Anzahlungskonto wuerde ein Konto entlasten, das fuer diesen Beleg
     *    nie bebucht wurde — Umsatz doppelt, Anzahlungskonto im Minus.
     *  - jeder andere Vorbeleg (kumulative Abrechnung): bereits als ERLOES
     *    gebucht. Hier darf nur der Zuwachs gebucht werden; eine Aufloesung
     *    gegen das Anzahlungskonto wuerde den Umsatz erst recht verdoppeln.
     *
     * Der Exporter kann die Belegart nicht selbst nachschlagen — er bekommt
     * die auszugebenden Belege, nicht deren Vorbelege. Ein Abzug ohne Eintrag
     * fuehrt deshalb zum Abbruch und wird NIE geraten: die falsche Annahme
     * bucht Umsatz doppelt oder gar nicht, und beides faellt erst beim
     * Kontenabgleich in der Kanzlei auf.
     *
     * Enthaelt ausschliesslich echte Rechnungs-ids. Externe Alt-Abschlaege
     * ('ext:<id>', externalAdvances.ts) stehen NICHT in public.invoices und
     * tragen gar keine Belegart; fuer sie entscheidet die einmalige
     * Einstellung InvoiceDatevConfig.externalAdvanceBooking, nicht diese Map.
     */
    deductionSources?: ReadonlyMap<string, DeductionSource>;
}

/**
 * Was der Exporter ueber einen abgezogenen Beleg wissen muss.
 *
 * exportedAt ist invoices.exported_at — der Exportvermerk, den
 * markInvoicesExported nach jeder Uebergabe an die Buchhaltung setzt. Fehlt
 * er, ist der Beleg ueber diesen Export nie in der Kanzlei gelandet; sein
 * eigener Export kommt erst noch und bucht dann nach der HEUTIGEN
 * Konfiguration.
 */
export interface DeductionSource {
    documentType: DocumentType;
    exportedAt?: string;
}

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const parseIsoDate = (value: string | undefined, context: string) => {
    const match = ISO_DATE_PATTERN.exec(value ?? '');
    if (!match) {
        throw new Error(`Datum fehlt oder ist unlesbar (${context}): ${value ?? '—'}`);
    }
    return { year: match[1], month: match[2], day: match[3] };
};

/** Belegdatum im Buchungsstapel: TTMM ohne Jahr, das Jahr leitet DATEV aus dem
 * Zeitraum der Kopfzeile ab. Durch die Referenz bestaetigt (dort z.B. "2904"). */
export const formatDayMonth = (value: string | undefined, context: string): string => {
    const { day, month } = parseIsoDate(value, context);
    return `${day}${month}`;
};

/** Achtstelliges Datum TTMMJJJJ, z.B. fuer das Leistungsdatum. */
export const formatDayMonthYear = (value: string | undefined, context: string): string => {
    const { year, month, day } = parseIsoDate(value, context);
    return `${day}${month}${year}`;
};

/**
 * Sechsstelliges Datum TTMMJJ fuer Belegfeld 2 — die Faelligkeit, aus der die
 * Kanzlei den Offene-Posten-Lauf aufbaut. In der Referenz steht dort z.B.
 * "060526" fuer den 06.05.2026.
 *
 * Bewusst OHNE Wurf, anders als die anderen Datumshelfer: Invoice.dueDate ist
 * im Typ ein Pflichtfeld, im Altbestand aber nicht garantiert. Eine fehlende
 * Faelligkeit kostet die Kanzlei einen offenen Posten; ein Wurf an dieser
 * Stelle kostet den gesamten Monatsexport. Ein leerer Rueckgabewert laesst das
 * Feld ueber setField einfach leer.
 */
export const formatDayMonthShortYear = (value: string | undefined): string => {
    const match = ISO_DATE_PATTERN.exec(value ?? '');
    if (!match) {
        return '';
    }
    return `${match[3]}${match[2]}${match[1].slice(2)}`;
};

/** Achtstelliges Datum JJJJMMTT, wie es die Kopfzeile verlangt. */
export const formatYearMonthDay = (value: string | undefined, context: string): string => {
    const { year, month, day } = parseIsoDate(value, context);
    return `${year}${month}${day}`;
};

/**
 * Cent-Betrag als DATEV-Zahl: Dezimalkomma, immer zwei Nachkommastellen.
 *
 * Rein ganzzahlig gerechnet. Der naheliegende Weg (cents / 100).toFixed(2)
 * ginge ueber einen Float und liefert bei grossen Betraegen gerundete
 * Ergebnisse — bei einem festgeschriebenen Beleg ist das nicht hinnehmbar.
 *
 * Das Vorzeichen wird bewusst NICHT ausgegeben: die Richtung traegt in DATEV
 * das Soll/Haben-Kennzeichen, der Umsatz ist immer positiv.
 */
export const formatAmountForDatev = (cents: number): string => {
    if (!Number.isSafeInteger(cents)) {
        throw new Error(`Betrag ist kein ganzzahliger Cent-Wert: ${cents}`);
    }
    const absolute = Math.abs(cents);
    const euro = Math.trunc(absolute / 100);
    const fraction = absolute % 100;
    return `${euro},${String(fraction).padStart(2, '0')}`;
};

/**
 * Bereinigt einen Text fuer ein DATEV-Feld.
 *
 * Zeilenumbrueche und Semikolon muessen raus: beide wuerden die Satzstruktur
 * zerlegen. Anfuehrungszeichen werden nicht entfernt, sondern beim Schreiben
 * verdoppelt — sie sind zulaessig, nur maskierungspflichtig.
 */
export const sanitizeText = (value: string | undefined, maxLength: number): string => {
    const cleaned = (value ?? '')
        .replace(/[\r\n\t]+/g, ' ')
        .replace(/;/g, ',')
        .replace(/\s{2,}/g, ' ')
        .trim()
        .slice(0, maxLength);

    // Fuehrendes =, +, - oder @ entschaerfen.
    //
    // Der Buchungstext entsteht aus dem Kundennamen, also aus freier
    // Benutzereingabe. Vor dem Import in DATEV wird die Datei in der Praxis
    // haeufig in Excel oder LibreOffice geoeffnet — und dort wird ein Feld,
    // das mit einem dieser Zeichen beginnt, als FORMEL ausgewertet. Die
    // Anfuehrungszeichen der CSV schuetzen davor nicht, sie werden vorher
    // entfernt. Ein vorangestelltes Apostroph macht den Inhalt zu Text.
    return /^[=+\-@]/.test(cleaned) ? `'${cleaned}`.slice(0, maxLength) : cleaned;
};

/**
 * PRUEFEN: Belegfeld 1 nimmt die Rechnungsnummer auf. DATEV erlaubt hier nur
 * einen eingeschraenkten Zeichenvorrat; welcher genau, ist nicht verifiziert.
 * Hier wird konservativ auf Buchstaben, Ziffern und wenige Trennzeichen
 * gefiltert — unsere Nummernkreise (RE-2026-0001) fallen ohnehin darunter.
 */
export const sanitizeDocumentNumber = (value: string): string =>
    value.replace(/[^A-Za-z0-9$%&*+\-/ ]/g, '').slice(0, MAX_LENGTH_BELEGFELD_1);

/** Textfeld: in doppelte Anfuehrungszeichen, innenliegende verdoppelt. */
export const quoteField = (value: string): string => `"${value.replace(/"/g, '""')}"`;

/**
 * Reine Ziffernfelder der Kopfzeile.
 *
 * Berater- und Mandantennummer sind bei DATEV numerisch. Sie ungequotet und
 * ungeprueft in die Zeile zu schreiben ist die einzige Stelle, an der ein
 * Semikolon aus den Einstellungen die Feldstruktur zerlegen kann — und der
 * Sicherheitsgurt darunter zaehlt Array-Elemente und kann das nicht sehen.
 * Lieber laut scheitern als eine Datei erzeugen, die beim Steuerberater
 * verschobene Spalten einliest.
 *
 * Exportiert, weil der Debitorenstamm-Export (datevExtf.ts) dieselbe Pruefung
 * braucht: beide Dateien muessen dieselbe Berater- und Mandantennummer tragen,
 * sonst ordnet DATEV sie nicht demselben Mandanten zu.
 */
export const onlyDigits = (value: string | undefined, label: string): string => {
    const text = (value ?? '').trim();
    if (!/^\d*$/.test(text)) {
        throw new Error(
            `${label} darf nur Ziffern enthalten (gefunden: "${text}"). Bitte in den Einstellungen korrigieren.`,
        );
    }
    return text;
};

/**
 * Sachkontenlaenge der Kopfzeile (Feld 14).
 *
 * Derselbe Waechter wie bei Berater- und Mandantennummer und aus demselben
 * Grund: der Wert ging bisher ungeprueft ueber String() in die Zeile, und ein
 * nicht gepflegtes Feld schrieb dort woertlich "undefined". Genau das stand im
 * Produktivexport, und DATEV weist einen solchen Kopf ab.
 *
 * Es wird KEIN Vorgabewert geraten. Die Sachkontenlaenge bestimmt die
 * Stellenzahl der Personenkonten; ein falscher Wert erzeugt keinen
 * Formatfehler, sondern einen formal gueltigen Stapel mit falschen Konten —
 * und der faellt erst in der Kanzlei auf. Lieber laut scheitern.
 */
const accountLengthField = (value: number | undefined): string => {
    if (typeof value !== 'number' || !Number.isInteger(value) || value <= 0) {
        throw new Error(
            'Die Sachkontenlänge fehlt in den DATEV-Einstellungen (gefunden: ' +
                `"${String(value)}"). Sie steht unter Firmeneinstellungen → Rechnungen → ` +
                '„Steuerberater-Export (DATEV / Simba)“ und kommt aus der Kanzlei: sie ' +
                'bestimmt die Stellenzahl der Personenkonten (Personenkonto = ' +
                'Sachkontenlänge + 1). Ohne sie schreibt die Kopfzeile „undefined“ und ' +
                'DATEV lehnt den Import ab. Geraten wird der Wert nicht — ein falscher ' +
                'erzeugt einen formal gültigen Stapel mit falschen Konten.',
        );
    }
    return String(value);
};

// ═══════════════════════════════════════════════════════════════════════════
// Kopfzeile
// ═══════════════════════════════════════════════════════════════════════════

/** Zeitstempel der Kopfzeile: JJJJMMTTHHMMSSFFF. */
const formatCreatedAt = (date: Date): string => {
    const pad = (value: number, width: number) => String(value).padStart(width, '0');
    return (
        `${date.getFullYear()}${pad(date.getMonth() + 1, 2)}${pad(date.getDate(), 2)}` +
        `${pad(date.getHours(), 2)}${pad(date.getMinutes(), 2)}${pad(date.getSeconds(), 2)}` +
        `${pad(date.getMilliseconds(), 3)}`
    );
};

/**
 * Kompakte Zeitraumangabe fuer die Stapelbezeichnung (Kopfzeile Feld 17).
 *
 * Die fruehere Vorbelegung "Rechnungsausgang 2026-01-01 bis 2026-07-31" hatte
 * 35 Zeichen und wurde von der Kappung auf MAX_LENGTH_BEZEICHNUNG mitten im
 * Wort abgeschnitten: "Rechnungsausgang 2026-01-01 bi". Keine
 * Formatverletzung, aber genau die Sorte Schlamperei, die der Steuerberater
 * als Erstes sieht.
 *
 * Deshalb monatsgenau statt tagesgenau: die exakten Tage stehen ohnehin in
 * "Datum von"/"Datum bis" (Felder 15 und 16), die Bezeichnung muss den
 * Stapel nur benennen. Drei Formen, maximal 11 Zeichen:
 *   ein Monat:      "01/2026"
 *   ein Jahr:       "01-07/2026"
 *   Jahreswechsel:  "12/25-01/26"
 *
 * Exportiert, weil auch der Gutschriften-Export (creditNoteDatev.ts) seine
 * Bezeichnung daraus baut — zwei Formatierungen desselben Zeitraums liefen
 * auseinander. Die Kappung in buildHeaderRow bleibt als letzter Schutz
 * bestehen; sie greift nur noch, wenn ein Aufrufer eigenen Freitext uebergibt.
 */
export const datevPeriodLabel = (period: DatevExportPeriod): string => {
    const from = parseIsoDate(period.from, 'Zeitraum von');
    const to = parseIsoDate(period.to, 'Zeitraum bis');
    if (from.year !== to.year) {
        return `${from.month}/${from.year.slice(2)}-${to.month}/${to.year.slice(2)}`;
    }
    const months = from.month === to.month ? from.month : `${from.month}-${to.month}`;
    return `${months}/${from.year}`;
};

/**
 * Vorbelegung der Stapelbezeichnung des Rechnungsausgangs, immer <= 30
 * Zeichen: laengste Form ist "Rechnungsausgang 12/25-01/26" (28).
 */
export const datevBatchDescription = (period: DatevExportPeriod): string =>
    `Rechnungsausgang ${datevPeriodLabel(period)}`;

export const buildHeaderRow = (
    config: InvoiceDatevConfig,
    period: DatevExportPeriod,
    options: DatevExportOptions,
): string => {
    const from = formatYearMonthDay(period.from, 'Zeitraum von');
    const to = formatYearMonthDay(period.to, 'Zeitraum bis');
    const description = options.description ?? datevBatchDescription(period);

    // Reihenfolge MUSS EXTF_HEADER_FIELD_NAMES entsprechen. Der Zaehler
    // darunter prueft das, damit ein spaeterer Austausch der Namensliste nicht
    // stillschweigend eine Feldverschiebung erzeugt.
    const fields: string[] = [
        quoteField(EXTF_MARKER), //                                        1
        String(EXTF_VERSION), //                                           2
        String(FORMAT_CATEGORY_BUCHUNGSSTAPEL), //                         3
        quoteField(FORMAT_NAME_BUCHUNGSSTAPEL), //                         4
        String(FORMAT_VERSION_BUCHUNGSSTAPEL), //                          5
        formatCreatedAt(options.createdAt ?? new Date()), //               6
        '', //                                                             7  Importiert
        quoteField(ORIGIN_MARKER), //                                      8
        quoteField(sanitizeText(options.exportedBy, MAX_LENGTH_BEZEICHNUNG)), // 9
        '', //                                                            10  Importiert von
        // Beide gehen ungeprueft aus den Einstellungen in die Kopfzeile. Steht
        // dort ein Semikolon, bekommt die Zeile ein Feld ZU VIEL und ab
        // "Mandant" verschiebt sich alles: das Wirtschaftsjahr landet in der
        // Sachkontenlaenge, "Datum von" in "Datum bis". Die Laengenpruefung
        // weiter unten sieht das nicht — sie zaehlt die Array-Elemente VOR dem
        // join, also genau vor der Stelle, an der das Feld zerfaellt.
        onlyDigits(config.consultantNumber, 'Beraternummer'), //           11
        onlyDigits(config.clientNumber, 'Mandantennummer'), //             12
        formatYearMonthDay(config.fiscalYearStart, 'Wirtschaftsjahresbeginn'), // 13
        accountLengthField(config.accountLength), //                      14
        from, //                                                          15
        to, //                                                            16
        quoteField(sanitizeText(description, MAX_LENGTH_BEZEICHNUNG)), //  17
        '', //                                                            18  Diktatkuerzel
        String(BOOKING_TYPE_FIBU), //                                     19
        '', //                                                            20  Rechnungslegungszweck
        // PRUEFEN: Festschreibung in der KOPFZEILE. Die Referenz schreibt hier
        // '0'; wir schreiben bewusst '1', weil ein nicht festgeschriebener
        // Stapel in der Kanzlei nachtraeglich aenderbar und damit nicht
        // GoBD-konform ist. Fachliche Entscheidung, kein Formatfehler.
        '1', //                                                           21
        quoteField(CURRENCY_EUR), //                                      22
        '', //                                                            23
        '', //                                                            24  Derivatskennzeichen
        '', //                                                            25
        '', //                                                            26
        // PRUEFEN: SKR als zweistellige Zahl ('03'/'04') — Schreibweise unsicher.
        quoteField(config.skr === 'SKR03' ? '03' : '04'), //              27
        '', //                                                            28
        '', //                                                            29
        '', //                                                            30
        quoteField('OS-IA'), //                                           31
    ];

    if (fields.length !== EXTF_HEADER_FIELD_NAMES.length) {
        throw new Error(
            `EXTF-Kopfzeile hat ${fields.length} Felder, erwartet werden ` +
                `${EXTF_HEADER_FIELD_NAMES.length}.`,
        );
    }
    return fields.join(FIELD_SEPARATOR);
};

// ═══════════════════════════════════════════════════════════════════════════
// Buchungszeilen
// ═══════════════════════════════════════════════════════════════════════════

/** Spaltenname -> Position. Einmal gebaut, damit die Zeilen ueber Namen befuellbar sind. */
const COLUMN_INDEX = new Map<string, number>(
    BUCHUNGSSTAPEL_COLUMNS_V13.map((name, index) => [name, index]),
);

/**
 * Setzt ein Feld ueber den Spaltennamen.
 *
 * Wirft bei unbekanntem Namen. Das ist der Sicherheitsgurt fuer den Tag, an
 * dem BUCHUNGSSTAPEL_COLUMNS_V13 gegen die echte Formatbeschreibung getauscht
 * wird: heisst eine Spalte dort anders, scheitert der Export laut, statt den
 * Wert in die falsche Spalte zu schreiben.
 */
export const setField = (row: string[], column: string, value: string, quoted: boolean): void => {
    const index = COLUMN_INDEX.get(column);
    if (index === undefined) {
        throw new Error(
            `Unbekannte DATEV-Spalte "${column}". Wurde BUCHUNGSSTAPEL_COLUMNS_V13 ` +
                'ersetzt, ohne die Feldbelegung anzupassen?',
        );
    }
    row[index] = value === '' ? '' : quoted ? quoteField(value) : value;
};

/**
 * Erloeskonto zum Steuersatz.
 *
 * Wirft bei einem Satz ohne hinterlegtes Konto. Ein Ausweichkonto zu raten
 * waere hier der teuerste denkbare Fehler: die Buchung liefe formal durch und
 * landete auf dem falschen Erloeskonto.
 */
const revenueAccountForTaxRate = (taxRateBp: number, config: InvoiceDatevConfig): string => {
    const account =
        taxRateBp === 1900
            ? config.revenueAccount19
            : taxRateBp === 700
              ? config.revenueAccount7
              : taxRateBp === 0
                ? config.revenueAccount0
                : undefined;
    if (!account) {
        throw new Error(
            `Kein Erlöskonto für den Steuersatz ${taxRateBp / 100} % konfiguriert. ` +
                'Bitte in den DATEV-Einstellungen hinterlegen.',
        );
    }
    return account;
};

/**
 * Anzahlungskonto zum Steuersatz — dieselbe Bauart wie
 * revenueAccountForTaxRate, und aus demselben Grund.
 *
 * Die Kanzlei fuehrt je Satz ein eigenes Konto (SKR04 3272 fuer 19 %, 3271
 * fuer 7 %), weil bei Automatikkonten das KONTO den Steuerschluessel steuert.
 * Ein Sammelkonto fuer alle Saetze zoege deshalb fuer 7 % dieselbe
 * Umsatzsteuer wie fuer 19 %.
 *
 * Wirft bei einem Satz ohne hinterlegtes Konto und weicht NICHT auf ein
 * anderes aus: ein geratenes Anzahlungskonto ist derselbe teure Fehler wie ein
 * geratenes Erloeskonto — die Buchung liefe formal durch und faellt erst in
 * der Voranmeldung auf.
 */
const advanceAccountForTaxRate = (taxRateBp: number, config: InvoiceDatevConfig): string => {
    const account =
        taxRateBp === 1900
            ? config.advancePaymentAccount19
            : taxRateBp === 700
              ? config.advancePaymentAccount7
              : taxRateBp === 0
                ? config.advancePaymentAccount0
                : undefined;
    if (!account) {
        throw new Error(
            `Kein Konto für erhaltene Anzahlungen zum Steuersatz ${taxRateBp / 100} % ` +
                'konfiguriert (SKR04: 19 % = 3272, 7 % = 3271). Bitte in den ' +
                'DATEV-Einstellungen hinterlegen — auf ein Konto zu einem anderen ' +
                'Steuersatz gebucht, zöge die Kanzlei die falsche Umsatzsteuer.',
        );
    }
    return account;
};

/**
 * Ist ueberhaupt ein Anzahlungskonto gepflegt?
 *
 * Beantwortet mit "mindestens eines der drei gesetzt" und nicht "alle drei":
 * die Frage dahinter ist immer, ob der Betrieb auf die Anzahlungsbuchung
 * UMGESTELLT hat — und das ist ein Ereignis fuer den ganzen Mandanten, kein
 * Zustand je Steuersatz (siehe advancePaymentAccountSince). Wer nur mit 19 %
 * abrechnet, pflegt genau ein Konto und hat trotzdem umgestellt.
 *
 * Welches Konto eine einzelne Buchung braucht, entscheidet danach allein
 * advanceAccountForTaxRate — und das wirft, wenn ausgerechnet der benoetigte
 * Satz fehlt.
 */
const hasAdvancePaymentAccount = (config: InvoiceDatevConfig): boolean =>
    Boolean(
        config.advancePaymentAccount19 ||
            config.advancePaymentAccount7 ||
            config.advancePaymentAccount0,
    );

/**
 * PRUEFEN: Debitorenkonto aus der Kundennummer.
 *
 * Unsicher ist das Zusammenspiel von debtorAccountBase und customerNumber: ob
 * die Basis ein Praefix ist oder ein Startwert, auf den addiert wird, geht aus
 * der Konfiguration nicht hervor. Umgesetzt ist die Praefix-Lesart. Ebenfalls
 * ungeprueft ist, ob DATEV hier zwingend accountLength + 1 Stellen erwartet.
 *
 * Die Referenz hilft hier nicht weiter, sie widerspricht sogar: dort tragen
 * die Kunden Nummern mit Bindestrich (26-141) und werden auf ein davon
 * getrenntes numerisches Konto (10021) gebucht. Unsere Umsetzung leitet das
 * Konto direkt aus der Kundennummer ab und wirft, sobald sie nicht rein
 * numerisch ist.
 */
export const debtorAccountFor = (invoice: Invoice, config: InvoiceDatevConfig): string => {
    const customerNumber = invoice.customerSnapshot?.customerNumber?.trim();
    if (!customerNumber) {
        throw new Error(
            `Rechnung ${invoice.invoiceNumber ?? invoice.id}: keine Kundennummer im ` +
                'Snapshot — ohne Debitorenkonto ist keine Buchung möglich.',
        );
    }
    const account = `${config.debtorAccountBase ?? ''}${customerNumber}`;
    if (!/^\d+$/.test(account)) {
        throw new Error(
            `Rechnung ${invoice.invoiceNumber ?? invoice.id}: Debitorenkonto ` +
                `"${account}" ist nicht rein numerisch.`,
        );
    }
    return account;
};

/**
 * Kundenname des Belegs.
 *
 * Der Schnappschuss schlaegt das Feld am Beleg: er ist der beim Ausstellen
 * eingefrorene Stand und damit das, worueber abgerechnet wurde. Buchungstext
 * und Beleginfo lesen dieselbe Quelle, damit beide denselben Kunden nennen.
 */
const customerNameFor = (invoice: Invoice): string =>
    invoice.customerSnapshot?.name ?? invoice.customerName ?? '';

/**
 * Die betroffenen Projektnummern eines Belegs, ohne Wiederholung.
 *
 * Sie kommen aus details.projects und nur ersatzweise aus den Zeilen. Beides
 * nennt dieselben Projekte — details.projects wird beim Anlegen aus genau
 * diesen Zeilen gebildet und beim Ausstellen eingefroren (resolveProjectRefs)
 * —, aber der Vermerk am Beleg steht auch dann zur Verfuegung, wenn der
 * Aufrufer die Positionen gar nicht geladen hat. Die Zeilen bleiben als
 * Rueckfall fuer Altbelege, die noch keinen Vermerk tragen.
 */
const projectNumbersFor = (invoice: Invoice): string[] => {
    const fromDetails = (invoice.details?.projects ?? [])
        .map((project) => project.number)
        .filter((number): number is string => Boolean(number));
    return Array.from(
        new Set(
            fromDetails.length > 0
                ? fromDetails
                : (invoice.lines ?? [])
                      .map((line) => line.projectNumber)
                      .filter((number): number is string => Boolean(number)),
        ),
    );
};

/**
 * Der Leistungszeitraum in der kompakten Monatsschreibweise des Stapels.
 *
 * Bewusst derselbe Helfer wie fuer die Stapelbezeichnung (datevPeriodLabel)
 * und keine zweite Schreibweise: zwei Formen desselben Zeitraums auf demselben
 * Beleg liest in der Kanzlei niemand als denselben Zeitraum.
 *
 * Quelle ist performancePeriod — dieselbe, aus der Spalte 115 und das
 * Beleginfo-Paar 7 gespeist werden. Die drei duerfen nicht auseinanderlaufen,
 * sonst nennt ein Beleg drei Leistungszeitraeume.
 *
 * Ohne ENDE bleibt die Angabe leer, genau wie Spalte 115 und Paar 7: ein
 * Beginn allein ist kein Leistungszeitpunkt. Ist nur das Ende bekannt, nennt
 * das Label dessen Monat — die Punktleistung, die der Beleg dann auch ist.
 * Fehlt alles, wird nichts geschrieben und nichts geworfen; ein Altbeleg ohne
 * Zuordnung ist kein Grund, einen Monatsexport abzubrechen (dieselbe
 * Begruendung wie am Leistungsdatum selbst).
 */
const bookingPeriodLabel = (invoice: Invoice): string => {
    const { from, to } = performancePeriod(invoice);
    if (!to) return '';
    return datevPeriodLabel({ from: from ?? to, to });
};

/**
 * Buchungstext: Leistungszeitraum, Kundenname, Projektnummern — in dieser
 * Reihenfolge, auf Feldlaenge gekuerzt.
 *
 * Der ZEITRAUM STEHT VORN, und das ist der ganze Zweck der Angabe. Die Kanzlei
 * wertet das echte DATEV-Feld "Leistungsdatum" (Spalte 115) nicht aus — ihr
 * Importprotokoll meldet "Leistungsdatum aus Import wurde ignoriert, da
 * Buchungslogik nicht aktiv". Der Betrieb rechnet aber Teilleistungen ueber
 * Zeitraeume ab und braucht die periodengerechte Zuordnung; sichtbar ankommen
 * tut sie damit nur ueber den Buchungstext. Der wird hart auf 60 Zeichen
 * gekappt, und lange Kundennamen schoepfen das aus — im ersten
 * Produktivexport stand dort bereits "Landesbetrieb Strassenbau und Verkehr
 * Schleswig-Holstein Stan". Hinten angehaengt fiele also ausgerechnet die
 * Angabe weg, um die es geht.
 *
 * Gekuerzt wird stattdessen der KUNDENNAME, und zwar als Einziges. Er ist die
 * am leichtesten ersetzbare Angabe der Zeile: derselbe Name steht ungekuerzt
 * im Beleginfo-Paar 3, und die Zeile bucht ohnehin auf sein Debitorenkonto.
 * Die PROJEKTNUMMERN bleiben stehen, weil sie die einzige Bruecke der Buchung
 * zum Projekt sind — sie stehen sonst nirgends im Stapel, und ohne sie ist
 * eine Rueckfrage der Kanzlei nicht mehr beantwortbar. Reichen selbst dafuer
 * die 60 Zeichen nicht (viele Projekte auf einem Beleg), kappt erst der letzte
 * Schnitt die hinteren Projektnummern; der Zeitraum vorn bleibt in jedem Fall
 * unangetastet.
 *
 * `marker` steht VOR dem Zeitraum und ist der einzige Grund, warum dieser
 * nicht immer an erster Stelle steht — siehe advanceSettlementTextFor.
 *
 * `periodLabel` ersetzt den Belegzeitraum durch den Zeitraum DIESER Zeile.
 * Eine Monatszeile nennt damit "06/2026" statt "06-08/2026": sie bucht genau
 * einen Monat, und ein Zeitraum ueber drei waere auf ihr eine falsche Angabe.
 * Die Form ist dieselbe, weil derselbe Helfer sie bildet, und der Monat steht
 * wie der Zeitraum vorn und ueberlebt die Kappung.
 */
const composeBookingText = (invoice: Invoice, marker: string, periodLabel?: string): string => {
    const head = [marker, periodLabel ?? bookingPeriodLabel(invoice)].filter(Boolean).join(' ');
    const prefix = head ? `${head} ` : '';
    const projectNumbers = projectNumbersFor(invoice);
    const suffix = projectNumbers.length > 0 ? ` ${projectNumbers.join(' ')}` : '';
    // Was nach Zeitraum und Projektnummern uebrig bleibt, gehoert dem Namen.
    const room = MAX_LENGTH_BUCHUNGSTEXT - prefix.length - suffix.length;
    // sanitizeText auch auf dem Namensteil, nicht nur auf dem fertigen Feld:
    // der Name ist freie Eingabe, und ein fuehrendes = macht das Feld beim
    // Oeffnen in Excel zur Formel. Steht ein Zeitraum davor, beginnt das Feld
    // ohnehin mit einer Ziffer — ohne Zeitraum aber nicht, und dann ist genau
    // diese Entschaerfung der Schutz.
    const name = room > 0 ? sanitizeText(customerNameFor(invoice), room) : '';
    return sanitizeText(`${prefix}${name}${suffix}`, MAX_LENGTH_BUCHUNGSTEXT);
};

/**
 * Buchungstext der Erloeszeile: ohne Vermerk, der Zeitraum steht ganz vorn.
 * Bei einer Monatszeile steht dort deren Monat statt des Belegzeitraums.
 */
const bookingTextFor = (invoice: Invoice, periodLabel?: string): string =>
    composeBookingText(invoice, '', periodLabel);

/** Bruttobetrag einer Steuergruppe. Netto und Steuer sind je Gruppe konsistent. */
const grossOfTaxGroup = (group: TaxGroup): number => group.taxableNetCents + group.taxCents;

/**
 * Vermerk der Verrechnungszeile. Feste Laenge (20 Zeichen) — daran haengt die
 * Rechnung, dass der Zeitraum dahinter die Kappung ueberlebt.
 */
const ADVANCE_SETTLEMENT_MARKER = 'Verrechnung Abschlag';

/**
 * Buchungstext der Verrechnungszeile.
 *
 * Sie muss sich in der Kanzlei von der Erloeszeile desselben Belegs
 * unterscheiden lassen — beide tragen dieselbe Belegnummer, dasselbe
 * Belegdatum und denselben Debitor. Der Vermerk steht deshalb ganz vorn: er
 * ist das Einzige, was die beiden Zeilen auf einen Blick trennt, und er darf
 * nicht hinter einer anderen Angabe verschwinden.
 *
 * Der Leistungszeitraum steht auch hier, direkt hinter dem Vermerk. Beide
 * Zeilen sind EIN Beleg: Spalte 115 und Beleginfo-Paar 7 tragen den Zeitraum
 * an beiden Zeilen identisch, und fehlte er ausgerechnet im Buchungstext der
 * Verrechnungszeile, sahen zwei Zeilen desselben Belegs nach zwei
 * verschiedenen Zeitraeumen aus. Fachlich ist er hier ebenso am Platz: die
 * Zeile loest eine erhaltene Anzahlung in der Periode auf, in der die Leistung
 * erbracht wurde — genau die Zuordnung, um die es der Kanzlei geht.
 *
 * Die Kappung erreicht ihn nicht: Vermerk und Zeitraum sind zusammen
 * hoechstens 32 Zeichen lang, gekuerzt wird auch hier der Kundenname.
 */
const advanceSettlementTextFor = (invoice: Invoice): string =>
    composeBookingText(invoice, ADVANCE_SETTLEMENT_MARKER);

// ── Leistungsmonate und ihre Anteile ─────────────────────────────────────
//
// Der Betrieb rechnet Teilleistungen ueber Zeitraeume ab. Ein Beleg ueber Juni
// bis August gehoert deshalb nicht als ein Betrag mit einem Zeitraum in den
// Stapel, sondern je Monat als eigene Buchungszeile — nur so steht die Leistung
// in der Buchhaltung in der Periode, in der sie erbracht wurde, ohne dass die
// Kanzlei die Abgrenzung von Hand nachbaut.
//
// Quelle ist die Zuordnung am Beleg (InvoiceLine.allocations): sie sagt, welche
// Protokolleintraege mit welcher Teilmenge und welchem Leistungsdatum
// abgerechnet wurden. Das Projekt wird dafuer nicht geladen — der Beleg
// beantwortet die Frage selbst, auch wenn der Eintrag dort spaeter nicht mehr
// existiert.

/** Jahr und Monat aus einem ISO-Tag. Ohne Tag ist die Zuordnung undatiert. */
const ISO_MONTH_PATTERN = /^(\d{4})-(\d{2})-\d{2}$/;

/**
 * Ein Leistungsmonat und das, was in ihm zugeordnet ist.
 *
 * `from` und `to` sind TATSAECHLICHE Zuordnungstage, nie der Monatserste oder
 * der Monatsletzte: ein erfundener Kalendertag waere eine Behauptung ueber
 * einen Leistungszeitpunkt, und die Zuordnung kennt den echten.
 */
interface PerformanceMonth {
    /** 'JJJJ-MM'. */
    key: string;
    /** Fruehestes Zuordnungsdatum in diesem Monat, ISO-Tag. */
    from: string;
    /** Spaetestes Zuordnungsdatum in diesem Monat, ISO-Tag. */
    to: string;
    /** Verteilgewicht: Netto der in diesem Monat zugeordneten Teilmengen. */
    weightCents: number;
}

/**
 * Netto einer TEILMENGE einer Position — dieselbe Rechenart wie computeLineNet,
 * nur mit der Menge des Anteils.
 *
 * Bewusst KEIN zweiter Rechenweg: computeLineNet bildet das Zeilennetto als
 * Menge x Einzelpreis x Nachlass und rundet dabei EINMAL. Fuer die volle Menge
 * gibt diese Funktion deshalb genau computeLineNet zurueck, statt die Formel
 * ein zweites Mal auszurechnen — sonst koennten die beiden Wege bei einem
 * krummen Nachlass um einen Cent auseinanderlaufen.
 *
 * Der Nachlass kommt ueber effectiveLineDiscountPercent herein, weil ein als
 * fester Betrag erfasster Nachlass fuer die GANZE Zeile gilt und sich auf eine
 * Teilmenge nur ueber seinen Anteil umlegen laesst; genau dafuer ist die
 * Funktion in invoiceLogic gedacht.
 *
 * netCentsExplicit-Zeilen (Korrektur und Gutschrift) tragen einen Betrag, der
 * NICHT aus Menge x Einzelpreis folgt — dort benennen Menge und Preis nur die
 * urspruengliche Position. Ihr Anteil wird deshalb aus dem eingefrorenen
 * Zeilenbetrag anteilig gebildet und nie nachgerechnet.
 */
const netOfLineQuantity = (line: InvoiceLine, quantity: number): number => {
    const full = line.quantity ?? 0;
    if (full !== 0 && quantity === full) return computeLineNet(line);
    if (line.netCentsExplicit) {
        return full === 0 ? 0 : roundCents((computeLineNet(line) * quantity) / full);
    }
    return roundCents(
        quantity * (line.unitPriceCents ?? 0) * (1 - effectiveLineDiscountPercent(line) / 100),
    );
};

/**
 * Die Leistungsmonate EINES Steuersatzes, aufsteigend.
 *
 * Bezugsgroesse ist der Steuersatz, nicht der Beleg — wie bei Netto und Steuer
 * in den Paaren 5 und 6. Ein Beleg mit 19 % und 7 % wird deshalb je Satz
 * getrennt in Monate zerlegt.
 *
 * `null` heisst NICHT AUFTEILBAR, und die beiden Faelle dahinter sind bewusst
 * gleich behandelt:
 *  - keine Zuordnung zu diesem Satz. Der Altbestand hat sie nicht ueberall,
 *    und ein Monatsraster ist kein Grund, einen Monatsexport abzubrechen —
 *    dieselbe Begruendung wie beim Leistungszeitraum, der ebenfalls leer
 *    bleiben darf.
 *  - ein Anteil OHNE Datum. Er wird weder stillschweigend einem Monat
 *    zugeschlagen noch weggelassen: das eine erfindet Leistung in einem Monat,
 *    das andere laesst eine Aufteilung vollstaendig aussehen, obwohl ein Teil
 *    fehlt. Solange auch nur ein Anteil undatiert ist, ist die Frage "welcher
 *    Monat" nicht beantwortbar, und dann wird sie gar nicht beantwortet.
 * In beiden Faellen bucht der Beleg unveraendert eine Zeile je Steuersatz, mit
 * dem Zeitraum des ganzen Belegs.
 */
const performanceMonthsForRate = (
    invoice: Invoice,
    taxRateBp: number,
): PerformanceMonth[] | null => {
    const byMonth = new Map<string, PerformanceMonth>();
    for (const line of invoice.lines ?? []) {
        if (line.taxRateBp !== taxRateBp) continue;
        if (!CONTRIBUTES_TO_NET.has(line.kind)) continue;
        for (const allocation of line.allocations ?? []) {
            const date = allocation.date ?? '';
            if (!ISO_MONTH_PATTERN.test(date)) return null;
            const share = netOfLineQuantity(line, allocation.quantity);
            const known = byMonth.get(date.slice(0, 7));
            if (!known) {
                byMonth.set(date.slice(0, 7), {
                    key: date.slice(0, 7),
                    from: date,
                    to: date,
                    weightCents: share,
                });
                continue;
            }
            if (date < known.from) known.from = date;
            if (date > known.to) known.to = date;
            known.weightCents += share;
        }
    }
    if (byMonth.size === 0) return null;
    return Array.from(byMonth.values()).sort((a, b) => a.key.localeCompare(b.key));
};

/**
 * Verteilt einen Cent-Betrag ueber Gewichte, ohne dass ein Rest verloren geht.
 *
 * Gerechnet wird ueber die LAUFENDE Summe: jeder Monat bekommt die Differenz
 * zwischen dem gerundeten Sollstand bis zu ihm und dem bereits vergebenen
 * Betrag. Der letzte Monat bekommt den Rest zum Zielbetrag zugewiesen, statt
 * ihn erneut zu runden — damit ist die Summe der Monatsbetraege exakt der
 * Zielbetrag, und ein Rundungsrest kann weder verschwinden noch doppelt
 * auftauchen. Jeder einzelne Monat weicht dabei um weniger als einen Cent von
 * seinem rechnerischen Anteil ab.
 *
 * Das traegt auch negative Gewichte (Storno, Gutschrift) und gemischte
 * Vorzeichen, weil nirgends auf Betraege ohne Vorzeichen ausgewichen wird.
 * Heben sich alle Gewichte auf, ist nichts verteilbar — dann gibt es keine
 * Aufteilung, statt einen Betrag auf eine Null-Summe zu beziehen.
 */
const spreadOverWeights = (targetCents: number, weights: number[]): number[] => {
    const total = weights.reduce((sum, weight) => sum + weight, 0);
    if (total === 0) return [];
    const amounts: number[] = [];
    let cumulativeWeight = 0;
    let assigned = 0;
    weights.forEach((weight, index) => {
        cumulativeWeight += weight;
        const cumulativeTarget =
            index === weights.length - 1
                ? targetCents
                : roundCents((targetCents * cumulativeWeight) / total);
        amounts.push(cumulativeTarget - assigned);
        assigned = cumulativeTarget;
    });
    return amounts;
};

/** Was eine Monatszeile bucht. Netto plus Steuer ergeben immer ihr Brutto. */
interface MonthShare {
    month: PerformanceMonth;
    grossCents: number;
    netCents: number;
    taxCents: number;
}

/**
 * Zerlegt eine Erloeszeile in ihre Monatsanteile — rundungsfrei in allen drei
 * Betraegen.
 *
 * BRUTTO wird ueber die Gewichte der Monate verteilt und ergibt in der Summe
 * exakt den Betrag, den die ungeteilte Zeile gebucht haette. Daran haengt die
 * Invariante des ganzen Exports: die Summe der Debitorenbewegungen eines
 * Belegs bleibt gross − prepaid (die Forderung des Belegs, siehe BookingLeg),
 * egal in wie viele Monate er zerfaellt.
 *
 * NETTO wird anschliessend ueber die BRUTTOBETRAEGE der Monate verteilt, nicht
 * ein zweites Mal ueber die Gewichte. Zwei Gruende: der Schluessel ist damit
 * genau das, was die Zeilen tatsaechlich buchen, und ein Monat ohne Brutto
 * bekommt kein Netto — sonst stuende neben einem Umsatz von 0,00 ein
 * Nettobetrag, den er nicht erklaert.
 *
 * STEUER ist die Differenz aus beiden und wird nirgends gerundet. Damit gilt
 * auf jeder Zeile Netto + Steuer = Umsatz, und in der Summe ueber alle Monate
 * ergibt die Steuer genau die der ungeteilten Zeile — Brutto und Netto sind
 * beide exakt, also ist es ihre Differenz auch.
 *
 * Ein Monat, dessen Brutto auf 0,00 faellt, bekommt gar keine Zeile: eine
 * Buchung ueber nichts ist keine Buchung. Das ist dieselbe Regel, nach der
 * schon bisher eine Erloeszeile ueber 0,00 entfaellt.
 *
 * `null` heisst "nicht aufteilbar" und fuehrt zurueck auf die eine Zeile mit
 * dem Belegzeitraum: die Gewichte heben sich auf, es gibt also keinen
 * Verteilschluessel, und ein geratener waere schlechter als keine Aufteilung.
 */
const monthlyShares = (
    grossCents: number,
    netCents: number,
    months: PerformanceMonth[],
): MonthShare[] | null => {
    const grossParts = spreadOverWeights(
        grossCents,
        months.map((month) => month.weightCents),
    );
    if (grossParts.length !== months.length) return null;

    const booked = months
        .map((month, index) => ({ month, grossCents: grossParts[index] }))
        .filter((share) => share.grossCents !== 0);
    if (booked.length === 0) return null;

    const netParts = spreadOverWeights(
        netCents,
        booked.map((share) => share.grossCents),
    );
    if (netParts.length !== booked.length) return null;

    return booked.map((share, index) => ({
        month: share.month,
        grossCents: share.grossCents,
        netCents: netParts[index],
        taxCents: share.grossCents - netParts[index],
    }));
};

/**
 * Eine einzelne Buchung eines Belegs.
 *
 * debtorCents ist die DEBITORENBEWEGUNG mit Vorzeichen, Soll positiv. Daran
 * haengt die Invariante des ganzen Exports: die Summe der debtorCents eines
 * Belegs muss grossTotalCents − prepaidCents ergeben — genau das, was der
 * Kunde aus diesem Beleg schuldet. Solange das gilt, kann keine Buchungszeile
 * Umsatz doppelt oder gar nicht fordern.
 *
 * Bewusst NICHT totals.payableCents: der Sicherheitseinbehalt mindert nur den
 * Zahlbetrag, nicht die Forderung (docs/sicherheitseinbehalt-plan.md §9). Der
 * Umsatz wird voll gebucht, der Einbehalt bleibt offener Posten auf dem
 * Debitor, und die Auszahlung mit der Schlussrechnung bucht nichts Neues —
 * die Forderung besteht schon. Ohne Einbehalt sind beide Groessen gleich.
 */
interface BookingLeg {
    /** Erloes- oder Anzahlungskonto, beides zum Steuersatz der Zeile. */
    counterAccount: string;
    debtorCents: number;
    bookingText: string;
    /**
     * Steuersatz DIESER Zeile in Basispunkten. Er steht als
     * "Umsatzsteuerprozent" in der Beleginfo — ein Beleg mit 19 % und 7 %
     * erzeugt zwei Zeilen, und jede nennt ihren eigenen Satz, nicht den des
     * Belegs.
     */
    taxRateBp: number;
    /**
     * Netto und Steuer DIESER Zeile fuer die Beleginfo. Beide fehlen an der
     * Verrechnungszeile gegen das Anzahlungskonto: sie loest eine erhaltene
     * Anzahlung auf und traegt gar keinen Erloes. Ein aus ihrem Bruttobetrag
     * zurueckgerechnetes Netto waere eine erfundene Zahl, die neben der
     * Erloeszeile desselben Belegs wie ein zweiter Umsatz aussaehe.
     */
    netCents?: number;
    taxCents?: number;
    /**
     * Der Leistungsmonat, dessen Anteil diese Zeile bucht — gesetzt, sobald
     * der Beleg in Monatszeilen zerlegt wurde.
     *
     * Er speist die Zeitraumfelder DIESER Zeile: Spalte 115 traegt dann das
     * spaeteste Zuordnungsdatum des Monats, Paar 7 dessen Spanne und der
     * Buchungstext dessen Monatsbezeichnung. Ohne ihn bleibt es beim Zeitraum
     * des ganzen Belegs.
     */
    month?: PerformanceMonth;
    /**
     * Der Monat der Beleginfo-Aufstellung (Paar 4) als 'JJJJ-MM'.
     *
     * An einer Monatszeile derselbe wie `month`. Er steht aber AUCH an einer
     * ungeteilten Zeile, deren Zuordnung nur einen einzigen Monat nennt: die
     * Aufstellung sagt dann dasselbe wie zuvor, waehrend die Zeitraumfelder
     * unveraendert dem Beleg folgen — siehe bookingLegsForInvoice.
     */
    breakdownMonth?: string;
}

/**
 * Netto und Steuer, die zum gebuchten Betrag DIESER Zeile passen.
 *
 * Die Steuergruppe des Belegs taugt nur, solange die Zeile ihren vollen
 * Bruttobetrag bucht. Zieht der Beleg eine Vorrechnung ab, bucht die
 * Erloeszeile nur den Zuwachs — die Gruppenwerte waeren dann eine Aussage
 * ueber den Gesamtstand und stuenden neben einem Umsatz, den sie nicht
 * erklaeren. Deshalb: unveraendert die Gruppenwerte, wenn die Zeile die ganze
 * Gruppe bucht, sonst der Bruttobetrag der Zeile nach ihrem Steuersatz
 * aufgeteilt. In beiden Faellen ergeben Netto und Steuer exakt den Umsatz der
 * Zeile — mehr behauptet die Beleginfo nicht.
 *
 * Symmetrisch gerundet, weil Math.round negative Werte aufwaerts rundet: ein
 * Storno soll dieselben Betraege zeigen wie die Rechnung, die er aufhebt.
 */
const legNetAndTax = (
    grossCents: number,
    taxRateBp: number,
    group: TaxGroup | undefined,
): { netCents: number; taxCents: number } => {
    if (group && grossOfTaxGroup(group) === grossCents) {
        return { netCents: group.taxableNetCents, taxCents: group.taxCents };
    }
    const net =
        Math.sign(grossCents) *
        Math.round((Math.abs(grossCents) * 10000) / (10000 + taxRateBp));
    return { netCents: net, taxCents: grossCents - net };
};

/**
 * Liegt der abgezogene Beleg auf dem Anzahlungskonto? Antwort aus Belegart
 * UND Exporthistorie.
 *
 * Die Belegart allein reicht nicht: eine Abschlagsrechnung, die exportiert
 * wurde, BEVOR das Anzahlungskonto eingerichtet war, hat dieser Export selbst
 * auf das Erloeskonto gebucht (Bestandsverhalten, siehe
 * bookingLegsForInvoice). Wuerde ihr Abzug jetzt gegen das Anzahlungskonto
 * aufgeloest, staende ihr Umsatz doppelt in den Erloesen und das
 * Anzahlungskonto liefe ins Minus — genau der Fehler, den der Umbau beseitigt
 * hat, nur in die Kontenumstellung verschoben. Und das ist kein Randfall,
 * sondern der Regelfall der Umstellung: JEDER vor ihr exportierte Abschlag
 * liegt auf dem Erloeskonto.
 *
 * Massgeblich ist deshalb der Exportvermerk des Abschlags gegen den
 * Umstellungszeitpunkt advancePaymentAccountSince (den die Verwaltung beim
 * Eintragen des Kontos automatisch stempelt):
 *  - nie exportiert            -> Anzahlung. Sein eigener Export kommt erst
 *                                 noch und bucht nach heutiger Konfiguration
 *                                 auf das Anzahlungskonto.
 *  - exportiert NACH since     -> Anzahlung, liegt auf dem Anzahlungskonto.
 *  - exportiert VOR since      -> liegt auf dem Erloeskonto und wird wie eine
 *                                 bereits gebuchte Vorrechnung verrechnet.
 *  - exportiert, since fehlt   -> nicht entscheidbar, Abbruch. Kommt nur vor,
 *                                 wenn das Konto an der Verwaltung vorbei in
 *                                 die Konfiguration geschrieben wurde.
 * Ist KEINES der Anzahlungskonten gepflegt, bleibt die Antwort "Anzahlung" —
 * der Aufrufer laeuft dann in den Riegel "kein Konto hinterlegt", nicht in
 * eine stille Erloesbuchung. Gefragt wird nach "mindestens eines der drei",
 * weil die Umstellung ein Ereignis fuer den ganzen Mandanten ist und nicht je
 * Steuersatz stattfindet.
 *
 * Grenze der Auskunft: sie beschreibt, wie DIESER Export den Beleg uebergeben
 * hat. Hat die Kanzlei Altbestaende von Hand aufs Anzahlungskonto umgebucht,
 * weiss die Anwendung davon nichts — siehe UNVERIFIED_DATEV_FIELDS. Und wer
 * das Konto loescht und spaeter neu eintraegt, setzt den Umstellungszeitpunkt
 * neu; dazwischen exportierte Abschlaege werden dann der Erloes-Seite
 * zugerechnet, was fuer die Leerlauf-Phase stimmt, fuer eine FRUEHERE
 * Konto-Phase aber nicht. Das Feld ist nicht zum Hin- und Herschalten da.
 *
 * Wirft, wenn der Quellbeleg nicht auflösbar ist. Das ist der teure Fall: ein
 * geratener Abzug bucht den Umsatz doppelt (als Anzahlung behandelte
 * Vorrechnung) oder gar nicht (als Vorrechnung behandelter Abschlag), und
 * beides faellt erst beim Kontenabgleich in der Kanzlei auf.
 */
const deductedIsAdvance = (
    config: InvoiceDatevConfig,
    deductionSources: ReadonlyMap<string, DeductionSource> | undefined,
    advanceInvoiceId: string,
    label: string,
    source: string,
): boolean => {
    const fact = deductionSources?.get(advanceInvoiceId);
    if (!fact) {
        throw new Error(
            `Rechnung ${label}: die Belegart des abgezogenen Belegs ${source} ist ` +
                'unbekannt. Ohne sie ist nicht entscheidbar, ob der Abzug eine erhaltene ' +
                'Anzahlung auflöst oder eine bereits gebuchte Vorrechnung ist — und ein ' +
                'geratener Abzug bucht den Umsatz doppelt oder gar nicht.',
        );
    }
    if (!ADVANCE_DOCUMENT_TYPES.has(fact.documentType)) return false;
    if (!fact.exportedAt) return true;
    if (!hasAdvancePaymentAccount(config)) return true;

    const exportedMs = Date.parse(fact.exportedAt);
    const sinceMs = config.advancePaymentAccountSince
        ? Date.parse(config.advancePaymentAccountSince)
        : Number.NaN;
    if (Number.isNaN(exportedMs) || Number.isNaN(sinceMs)) {
        throw new Error(
            `Rechnung ${label}: der Abschlag ${source} wurde bereits exportiert, aber es ` +
                'ist nicht feststellbar, ob das vor oder nach der Einrichtung des ' +
                'Anzahlungskontos geschah — davor hat der Export ihn auf das Erlöskonto ' +
                'gebucht, danach auf das Anzahlungskonto, und der Abzug muss dasselbe ' +
                'Konto treffen. Der Umstellungszeitpunkt wird beim Eintragen des Kontos ' +
                'in der Verwaltung automatisch vermerkt; er fehlt hier. Bitte das ' +
                'Anzahlungskonto in den DATEV-Einstellungen einmal löschen, speichern ' +
                'und neu eintragen — und mit der Kanzlei klären, wie die bereits ' +
                'übergebenen Abschläge dort gebucht sind.',
        );
    }
    return exportedMs >= sinceMs;
};

/**
 * Dasselbe fuer einen EXTERNEN Alt-Abschlag aus dem Vorsystem.
 *
 * Er steht in public.external_advances, nicht in public.invoices, und traegt
 * keine Belegart — die Information existiert im System nicht. Sie kommt aus
 * dem Vorsystem, gilt fuer den gesamten Altbestand gleichermassen und steht
 * deshalb als einmalige Einstellung in der Konfiguration.
 *
 * Kein stiller Rueckfall auf eine der beiden Lesarten: 'PRIOR_INVOICE' auf
 * eine echte Anzahlung angewandt weist den Umsatz zu niedrig aus, 'ADVANCE'
 * auf eine gewoehnliche Vorrechnung angewandt loest einen Anzahlungsbestand
 * auf, den dieser Export nie aufgebaut hat. Die Meldung nennt deshalb beide
 * betroffenen Belege und den Weg zur Antwort.
 */
const externalAdvanceIsAdvance = (
    config: InvoiceDatevConfig,
    label: string,
    source: string,
): boolean => {
    if (!config.externalAdvanceBooking) {
        throw new Error(
            `Rechnung ${label}: zieht den externen Alt-Abschlag ${source} ab, aber es ist ` +
                'nicht hinterlegt, wie das Vorsystem solche Altbelege gebucht hat. Die ' +
                'Angabe stammt aus dem Vorsystem bzw. von der Kanzlei, gilt für den ' +
                'gesamten Altbestand und wird in den DATEV-Einstellungen unter „Buchung ' +
                'der externen Alt-Abschläge“ einmalig eingetragen (erhaltene Anzahlung ' +
                'oder bereits als Erlös gebuchte Vorrechnung).',
        );
    }
    return config.externalAdvanceBooking === 'ADVANCE';
};

/**
 * Zerlegt einen Beleg in seine Buchungen.
 *
 * Der Fehler, den diese Funktion behebt: gebucht wurde je Steuergruppe der
 * volle Bruttobetrag der LEISTUNG. totals.taxGroups entsteht vor jedem Abzug
 * (netTotalCents ist laut types.ts die "Summe der Leistungszeilen vor
 * Abschlagsabzug"), prepaidCents und payableCents kamen hier gar nicht vor.
 * Beim kumulativen Abrechnungsmodell des Betriebs zieht praktisch jeder Beleg
 * seine Vorrechnungen ab — deren Umsatz wurde damit in jedem Folgebeleg
 * erneut gebucht.
 *
 * payableCents kommt hier weiterhin nicht vor, und das ist jetzt Absicht: ein
 * Sicherheitseinbehalt (totals.retentionCents) mindert nur den Zahlbetrag.
 * Gebucht wird die volle Forderung gross − prepaid; der Einbehalt bleibt
 * offener Posten auf dem Debitor, und die Auszahlung frueherer Einbehalte
 * (totals.retentionReleaseCents) auf der Schlussrechnung erzeugt KEINE Zeile.
 *
 * Je Steuersatz entstehen daraus:
 *   Erloeszeilen     Brutto der Gruppe abzueglich der Abzuege aus VORRECHNUNGEN
 *                    (deren Erloes steht bereits in der Buchhaltung), verteilt
 *                    auf die LEISTUNGSMONATE der Zuordnung — je Monat eine
 *                    Zeile, siehe monthlyShares
 *   Anzahlungszeile  die Abzuege aus ABSCHLAGSRECHNUNGEN, gegen das
 *                    Anzahlungskonto DIESES Steuersatzes und mit umgekehrter
 *                    Debitorenseite (Anzahlungskonto im Soll an Debitor im
 *                    Haben)
 * Eine Zeile ueber 0,00 entfaellt.
 *
 * Die VERRECHNUNGSZEILE wird NICHT auf Monate verteilt. Sie bucht keine
 * Leistung, sondern loest eine erhaltene Anzahlung auf; ein Monatsraster
 * darauf waere erfunden, weil die Anzahlung gar keinen Leistungsmonat hat. Sie
 * bleibt eine Zeile mit dem Zeitraum des Belegs.
 *
 * Wer das liest und die Anzahlungskonten leer vorfindet: das ist so gewollt.
 * Der Betrieb rechnet TEILLEISTUNGEN ab (§ 13 Abs. 1 Nr. 1a UStG) — eine
 * abgegrenzte, abgenommene und damit ERBRACHTE Leistung, also echter Erloes,
 * mit dem die Umsatzsteuer entsteht. Eine Anzahlung ist das Gegenteil: noch
 * keine Leistung, nur Geld, und deshalb eine Verbindlichkeit. Der gesamte
 * Anzahlungspfad ist damit Vorsorge fuer einen Fall, der hier heute nicht
 * eintritt; er ist vollstaendig und richtig, nur ungenutzt. Ohne gepflegtes
 * Konto passiert nichts — der Riegel greift ausschliesslich dann, wenn
 * tatsaechlich ein Abschlagsabzug im Stapel steht.
 *
 * Gerechnet wird ueber die Steuersaetze, nicht ueber die Steuergruppen: ein
 * Abzug zu einem Satz, den die Leistung nicht mehr enthaelt, bekommt so eine
 * eigene Zeile, statt lautlos unter den Tisch zu fallen und die Invariante zu
 * brechen. Die Reihenfolge der Steuergruppen bleibt fuehrend, damit sich am
 * Aufbau bestehender Stapel nichts verschiebt.
 */
const bookingLegsForInvoice = (
    invoice: Invoice,
    config: InvoiceDatevConfig,
    deductionSources: ReadonlyMap<string, DeductionSource> | undefined,
): BookingLeg[] => {
    const label = invoice.invoiceNumber ?? invoice.id;
    const fromAdvances = new Map<number, number>();
    const fromPriorInvoices = new Map<number, number>();

    for (const deduction of invoice.advanceDeductions ?? []) {
        const source = deduction.invoiceNumber || deduction.advanceInvoiceId;

        // Externe Alt-Abschlaege stehen nicht in public.invoices und tragen
        // keine Belegart — die Auskunft kommt einmalig aus dem Vorsystem und
        // steht dann in der Konfiguration. Ohne sie wird nicht geraten.
        const isAdvance = isExternalAdvanceId(deduction.advanceInvoiceId)
            ? externalAdvanceIsAdvance(config, label, source)
            : deductedIsAdvance(
                  config,
                  deductionSources,
                  deduction.advanceInvoiceId,
                  label,
                  source,
              );

        const target = isAdvance ? fromAdvances : fromPriorInvoices;
        target.set(
            deduction.taxRateBp,
            (target.get(deduction.taxRateBp) ?? 0) + deduction.grossCents,
        );
    }

    if (fromAdvances.size > 0 && !hasAdvancePaymentAccount(config)) {
        throw new Error(
            `Rechnung ${label}: zieht eine Abschlagsrechnung ab, aber es ist kein Konto für ` +
                'erhaltene Anzahlungen hinterlegt (SKR04: 19 % = 3272, 7 % = 3271). Bitte in ' +
                'den DATEV-Einstellungen unter „Konten der erhaltenen Anzahlungen“ eintragen — ' +
                'auf ein Erlöskonto gebucht, stünde der Abschlag ein zweites Mal im Umsatz.',
        );
    }

    // Die Abschlagsrechnung SELBST ist kein Erloes: sie bucht gegen das
    // Anzahlungskonto ihres Steuersatzes. Ohne jedes gepflegte Konto bleibt es
    // beim bisherigen Verhalten (Erloeskonto) — der Umstieg ist eine
    // Entscheidung mit der Kanzlei und darf keinen laufenden Export abbrechen.
    // Ist umgestellt, fehlt aber ausgerechnet das Konto zum Satz dieser
    // Gruppe, scheitert advanceAccountForTaxRate laut, statt auf das Konto
    // eines anderen Satzes auszuweichen.
    const bookOwnGrossToAdvanceAccount =
        ADVANCE_DOCUMENT_TYPES.has(invoice.documentType) && hasAdvancePaymentAccount(config);

    const rates = invoice.totals.taxGroups.map((group) => group.taxRateBp);
    for (const rate of [...fromAdvances.keys(), ...fromPriorInvoices.keys()]) {
        if (!rates.includes(rate)) rates.push(rate);
    }

    const legs: BookingLeg[] = [];
    for (const rate of rates) {
        const group = invoice.totals.taxGroups.find((g) => g.taxRateBp === rate);
        const gross = group ? grossOfTaxGroup(group) : 0;

        const revenueCents = gross - (fromPriorInvoices.get(rate) ?? 0);
        if (revenueCents !== 0) {
            const counterAccount = bookOwnGrossToAdvanceAccount
                ? advanceAccountForTaxRate(rate, config)
                : revenueAccountForTaxRate(rate, config);
            const { netCents, taxCents } = legNetAndTax(revenueCents, rate, group);
            const months = performanceMonthsForRate(invoice, rate);

            // Aufgeteilt wird erst ab ZWEI Monaten. Bei einem einzigen gaebe es
            // nichts zu verteilen, und die Zeitraumfelder blieben besser, wo
            // sie sind: performancePeriod kennt mehr Quellen als die Zuordnung
            // (etwa den eingefrorenen Leistungszeitraum am Beleg) und beugt
            // damit dem Fall des Betreibers vor, in dem die Zuordnung im April
            // endet, geleistet aber bis August wurde. Die Monatsangabe in
            // Paar 4 steht trotzdem — sie sagt dasselbe wie bisher.
            //
            // AB zwei Monaten gilt bewusst das Umgekehrte: jede Zeile traegt
            // die Daten IHRES Monats aus der Zuordnung, und der eingefrorene
            // Leistungszeitraum des Belegs erscheint auf keiner Zeile mehr —
            // ein Beleg, dessen eine Zeile bisher den 10.08. nannte, kann
            // geteilt mit dem 22.04. enden. Die Voranmeldungsperiode
            // verschiebt das nicht: die folgt bei dieser Kanzlei dem
            // BELEGDATUM, ihr Import ignoriert Spalte 115 ("Leistungsdatum
            // aus Import wurde ignoriert, da Buchungslogik nicht aktiv").
            const shares =
                months && months.length > 1
                    ? monthlyShares(revenueCents, netCents, months)
                    : null;

            if (shares) {
                for (const share of shares) {
                    legs.push({
                        counterAccount,
                        debtorCents: share.grossCents,
                        // Derselbe Helfer wie fuer den Belegzeitraum, damit
                        // beide Formen nicht auseinanderlaufen: ein einzelner
                        // Monat wird dort zu "06/2026".
                        bookingText: bookingTextFor(
                            invoice,
                            datevPeriodLabel({ from: share.month.from, to: share.month.to }),
                        ),
                        taxRateBp: rate,
                        netCents: share.netCents,
                        taxCents: share.taxCents,
                        month: share.month,
                        breakdownMonth: share.month.key,
                    });
                }
            } else {
                legs.push({
                    counterAccount,
                    debtorCents: revenueCents,
                    bookingText: bookingTextFor(invoice),
                    taxRateBp: rate,
                    netCents,
                    taxCents,
                    breakdownMonth: months?.length === 1 ? months[0].key : undefined,
                });
            }
        }

        const advanceCents = fromAdvances.get(rate) ?? 0;
        if (advanceCents !== 0) {
            legs.push({
                // Aufgeloest wird auf dem Anzahlungskonto DIESES Steuersatzes.
                // Ein Beleg mit Abzuegen zu 19 % und 7 % erzeugt deshalb zwei
                // Verrechnungszeilen auf zwei verschiedene Konten.
                counterAccount: advanceAccountForTaxRate(rate, config),
                // Entgegengesetzte Seite der Erloeszeile. Bewusst ueber das
                // Vorzeichen und nicht ueber ein festes 'H': bei einem
                // negativen Beleg (Storno, Gutschrift) sind auch die Abzuege
                // negativ, und die Verrechnung muss mitkippen.
                debtorCents: -advanceCents,
                bookingText: advanceSettlementTextFor(invoice),
                // Bewusst OHNE netCents/taxCents: hier wird kein Erloes
                // gebucht, sondern eine erhaltene Anzahlung aufgeloest.
                taxRateBp: rate,
            });
        }
    }

    return legs;
};

/**
 * Steuersatz als Prozentzahl fuer die Beleginfo. Die Referenz schreibt "19",
 * also ohne Prozentzeichen und ohne Nachkommastellen; ein krummer Satz bekaeme
 * das deutsche Dezimalkomma, damit das Feld nicht zwei Schreibweisen mischt.
 */
const formatTaxPercent = (taxRateBp: number): string =>
    String(taxRateBp / 100).replace('.', ',');

/**
 * Ein Leistungszeitraum als LESBARER Freitext fuer die Beleginfo.
 *
 * Zwei verschiedene Tage ergeben "TTMMJJJJ - TTMMJJJJ", Bindestrich mit je
 * einem Leerzeichen, exakt wie in der Referenz. Gleicher Von- und Bis-Tag ist
 * eine Punktleistung und wird als ein einzelnes Datum geschrieben — ein
 * Zeitraum von einem Tag auf sich selbst waere nur Rauschen.
 *
 * Eine Stelle fuer beide Aufrufer — den Belegzeitraum und den Zeitraum einer
 * Monatszeile —, damit derselbe Beleg nicht zwei Schreibweisen desselben
 * Zeitraums traegt.
 */
const datePeriodText = (from: string | undefined, to: string): string => {
    const end = formatDayMonthYear(to, 'Leistungsdatum');
    if (!from || from === to) return end;
    return `${formatDayMonthYear(from, 'Beginn des Leistungszeitraums')} - ${end}`;
};

/**
 * Der Leistungszeitraum DES BELEGS — fuer jede Zeile, die keinen eigenen Monat
 * bucht.
 *
 * Ohne ENDE bleibt der Freitext leer, auch wenn ein Beginn bekannt waere: die
 * Spalte 115 "Leistungsdatum" ist dann ebenfalls leer, und ein Beginn allein
 * unter der Bezeichnung "Leistungsdatum" behauptete eine Angabe, die das echte
 * DATEV-Feld daneben nicht traegt. Fehlt alles, wird nichts geschrieben und
 * nichts geworfen — das Belegdatum steht ohnehin daneben, dieselbe Begruendung
 * wie beim Leistungsdatum selbst.
 */
const performancePeriodText = (invoice: Invoice): string => {
    const { from, to } = performancePeriod(invoice);
    if (!to) return '';
    return datePeriodText(from, to);
};

/** Beschriftung des Paares 4. Die Kanzlei hat genau danach gefragt. */
const MONTHLY_BREAKDOWN_LABEL = 'Leistung je Monat';

/**
 * Die Monatsangabe DIESER Buchungszeile: ihr eigener Leistungsmonat und das
 * Netto, das sie in ihm bucht ("06/2026: 780,00").
 *
 * Frueher stand hier die Aufstellung des GANZEN Belegs. Seit jede Zeile einen
 * Monat bucht, waere sie falsch am Platz: dieselbe Liste stuende auf jeder
 * Zeile und widerspraeche der Zeile, auf der sie steht. Die Beschriftung
 * bleibt, weil die Kanzlei genau danach gefragt hat.
 *
 * Leer bleibt das Paar — Art UND Inhalt — an der Verrechnungszeile gegen das
 * Anzahlungskonto (sie fuehrt aus gutem Grund weder Netto noch Steuer, siehe
 * BookingLeg) und an jeder Zeile, deren Monat nicht feststeht: fehlende oder
 * undatierte Zuordnung, siehe performanceMonthsForRate.
 *
 * Der Betrag steht OHNE Vorzeichen — wie jeder Betrag im Stapel. Hier stand
 * einmal ein vorzeichenbehafteter, damit ein Storno im Freitext nicht aussieht
 * wie die Rechnung, die er aufhebt. Das erzeugte einen Widerspruch in derselben
 * Zeile: "Nettobetrag" und "Steuerbetrag" daneben nehmen den Absolutbetrag
 * (formatAmountForDatev, DATEV-Konvention), und seit jede Zeile genau einen
 * Monat bucht, ist der Monatsbetrag buchstaeblich dasselbe Netto — auf einem
 * Storno stand "-1440,00" unmittelbar neben "1440". Die Richtung ist ohnehin
 * eindeutig und steht eine Spalte weiter vorn: das Soll/Haben-Kennzeichen gilt
 * fuer die ganze Zeile, also auch fuer die Beleginfo, die sie beschreibt.
 */
const monthlyBreakdownText = (leg: BookingLeg): string => {
    if (leg.breakdownMonth === undefined || leg.netCents === undefined) return '';
    const [year, month] = [leg.breakdownMonth.slice(0, 4), leg.breakdownMonth.slice(5, 7)];
    return `${month}/${year}: ${formatAmountForDatev(leg.netCents)}`;
};

const buildBookingRow = (
    invoice: Invoice,
    leg: BookingLeg,
    config: InvoiceDatevConfig,
    belegLinkIds: ReadonlySet<string> | undefined,
): string => {
    const row: string[] = new Array(BUCHUNGSSTAPEL_COLUMNS_V13.length).fill('');
    const gross = leg.debtorCents;

    // Die Richtung ergibt sich AUSSCHLIESSLICH aus dem Vorzeichen.
    //
    // Frueher wurde zusaetzlich die Belegart herangezogen und mit dem
    // Vorzeichen verknuepft. Das war falsch: Storno und Gutschriften werden im
    // ganzen Modul mit NEGATIVEN Mengen erfasst, tragen also bereits ein
    // negatives Brutto. Die doppelte Verneinung drehte sie zurueck aufs Soll —
    // dieselbe Seite und derselbe Betrag wie die Ursprungsrechnung. Der Storno
    // hob die Buchung nicht auf, er verdoppelte sie, ohne dass ein Fehler
    // entstand.
    const creditSide = gross < 0;

    setField(row, 'Umsatz', formatAmountForDatev(gross), false);
    setField(row, 'Soll-/Haben-Kennzeichen', creditSide ? 'H' : 'S', true);
    setField(row, 'WKZ Umsatz', CURRENCY_EUR, true);
    setField(row, 'Konto', debtorAccountFor(invoice, config), false);
    setField(row, 'Gegenkonto (ohne BU-Schlüssel)', leg.counterAccount, false);
    // PRUEFEN: BU-Schluessel bleibt leer, weil der Steuersatz bereits ueber das
    // Erloeskonto gesteuert wird. Arbeitet die Kanzlei mit Automatikkonten,
    // ist das richtig; sonst muss hier der Steuerschluessel stehen.
    setField(row, 'Belegdatum', formatDayMonth(invoice.invoiceDate, 'Belegdatum'), false);
    setField(
        row,
        'Belegfeld 1',
        sanitizeDocumentNumber(invoice.invoiceNumber ?? ''),
        true,
    );

    // Belegfeld 2 = Faelligkeit als TTMMJJ. Ohne sie hat die Kanzlei im
    // Offene-Posten-Lauf kein Faelligkeitsdatum und kann nicht mahnen.
    const dueDate = formatDayMonthShortYear(invoice.dueDate);
    if (dueDate) {
        setField(row, 'Belegfeld 2', dueDate, true);
    }

    // Beleglink verknuepft die Buchung mit dem PDF aus dem Belegbild-Archiv.
    // Die GUID ist die Rechnungs-id selbst (in der Datenbank ein uuid) und
    // damit ueber wiederholte Exporte stabil.
    //
    // Gesetzt wird er NUR fuer Belege, deren PDF im selben Paket liegt. Ein
    // Verweis auf ein Dokument, das nicht mitkommt, ist keine Verknuepfung,
    // sondern eine Behauptung — und ob DATEV einen ins Leere laufenden
    // Beleglink stillschweigend ignoriert oder den Import anmeckert, ist
    // nicht geprueft. Die Frage stellt sich hier gar nicht erst.
    if (belegLinkIds?.has(invoice.id)) {
        setField(row, 'Beleglink', `BEDI "${documentGuidForInvoice(invoice)}"`, true);
    }

    setField(row, 'Buchungstext', leg.bookingText, true);
    // Festschreibung immer '1': ein nicht festgeschriebener Stapel ist in der
    // Kanzlei nachtraeglich aenderbar und damit nicht GoBD-konform.
    setField(row, 'Festschreibung', '1', false);

    // Leistungsdatum = Ende des LEISTUNGSZEITRAUMS, also die letzte
    // tatsaechlich abgerechnete Leistung — dieselbe Angabe, die der Beleg in
    // seiner Zeile "Leistungszeitraum" nennt.
    //
    // Bewusst NICHT der Abrechnungszeitraum: §14 Abs. 4 Nr. 6 UStG meint den
    // Zeitpunkt der LEISTUNG, nicht den Zeitraum, ueber den abgerechnet wird.
    // Ein Feld, das nur den Andruck steuern soll, darf das Leistungsdatum
    // nicht verdraengen — der Buchungsstapel in der Kanzlei muss dasselbe
    // sagen wie das Blatt beim Kunden. Die Voranmeldungsperiode haengt an
    // dieser Spalte ohnehin nicht: die Kanzlei bucht nach dem BELEGDATUM,
    // ihr Import ignoriert Spalte 115 (siehe composeBookingText).
    //
    // performancePeriod faellt je Seite selbst bis auf deliveryDateTo zurueck;
    // ein Beleg ohne Zuordnung bucht deshalb unveraendert wie bisher. Fehlt
    // alles, bleibt das Feld leer — das Belegdatum steht ohnehin daneben.
    //
    // Eine MONATSZEILE traegt das spaeteste tatsaechliche Leistungsdatum ihres
    // Monats aus der Zuordnung, nicht den Monatsletzten: ein erfundener
    // Kalendertag waere eine Behauptung, und die Zuordnung kennt den echten
    // Tag.
    const serviceDate = leg.month ? leg.month.to : performancePeriod(invoice).to;
    if (serviceDate) {
        setField(row, 'Leistungsdatum', formatDayMonthYear(serviceDate, 'Leistungsdatum'), false);
    }

    // ── Beleginfo: die lesbaren Belegangaben ─────────────────────────────
    //
    // Anlass ist der LEISTUNGSZEITRAUM. Der Betrieb rechnet Teilleistungen
    // ueber Zeitraeume ab; Spalte 115 traegt davon nur das Ende, und ein Beleg
    // ueber Juni bis Juli sah im Stapel deshalb aus wie eine Punktleistung am
    // 31. Juli. Spalte 115 bleibt trotzdem PUNKTGENAU: sie ist das echte
    // DATEV-Feld, und ein Zeitraum darin waere weder Format noch Wahrheit.
    // (Die Voranmeldungsperiode steuert sie bei dieser Kanzlei nicht — der
    // Import ignoriert sie, gebucht wird nach Belegdatum; siehe
    // composeBookingText.) Die Referenz loest es genauso — das Feld bleibt
    // ein Tag, der lesbare Zeitraum steht als Freitext in der Beleginfo
    // ("01012026 - 31122026").
    //
    // SLOT-TREUE ist dabei die ganze Idee: jede Angabe steht im SELBEN Paar
    // wie in der Referenz, weil die Kanzlei sie dort sucht, wo sie sie kennt.
    // Paar 1 (Beschreibung) bleibt deshalb frei, obwohl es zur Verfuegung
    // stuende.
    //
    // Die einzige Angabe, die die Referenz nicht kennt, ist die Verteilung des
    // Zeilennettos auf die Monate. Sie steht in Paar 4, weil das dort das
    // einzige ungenutzte Paar ist — die uebrigen sieben tragen feste
    // Bedeutungen, und wer sie umsortiert, laesst die Kanzlei den Nettobetrag
    // dort suchen, wo bisher der Name stand.
    //
    // Ein Paar ohne Inhalt bleibt VOLLSTAENDIG leer, auch die Art-Spalte. Die
    // Referenz schreibt bei "Beschreibung" die Art ohne Inhalt; das wird
    // bewusst nicht nachgebaut. Eine Bezeichnung ohne Wert ist eine leere
    // Behauptung — sie laesst in der Kanzlei jemanden nach einem Inhalt
    // suchen, den es nie gab.
    const beleginfo = (pair: number, art: string, inhalt: string): void => {
        if (!inhalt) return;
        setField(row, `Beleginfo-Art ${pair}`, art, true);
        setField(
            row,
            `Beleginfo-Inhalt ${pair}`,
            sanitizeText(inhalt, MAX_LENGTH_BELEGINFO_INHALT),
            true,
        );
    };

    beleginfo(2, 'Umsatzsteuerprozent', formatTaxPercent(leg.taxRateBp));
    beleginfo(3, 'Name', customerNameFor(invoice));
    // Bezieht sich wie Netto und Steuer auf die ZEILE und nennt genau deren
    // Monat — siehe monthlyBreakdownText.
    beleginfo(4, MONTHLY_BREAKDOWN_LABEL, monthlyBreakdownText(leg));
    // Netto und Steuer fehlen an der Verrechnungszeile gegen das
    // Anzahlungskonto — siehe BookingLeg. Der Steuersatz darueber bleibt auch
    // dort stehen: er ist keine Behauptung ueber einen Erloes, sondern die
    // Angabe, auf welches der drei Anzahlungskonten die Zeile laeuft.
    if (leg.netCents !== undefined) {
        beleginfo(5, 'Nettobetrag', formatAmountForDatev(leg.netCents));
    }
    if (leg.taxCents !== undefined) {
        beleginfo(6, 'Steuerbetrag', formatAmountForDatev(leg.taxCents));
    }
    // Zeitraum, Name und Kundennummer stehen auch an der Verrechnungszeile:
    // beide Zeilen sind EIN Beleg und muessen sich in der Kanzlei als einer
    // lesen lassen. Die Verrechnungszeile bucht keinen Monat und nennt deshalb
    // weiter den Zeitraum des Belegs; eine Monatszeile nennt die Spanne IHRES
    // Monats, sonst stuende neben ihrem Umsatz ein Zeitraum, den sie nicht
    // bucht.
    beleginfo(
        7,
        'Leistungsdatum',
        leg.month ? datePeriodText(leg.month.from, leg.month.to) : performancePeriodText(invoice),
    );
    beleginfo(8, 'Kundennummer', invoice.customerSnapshot?.customerNumber?.trim() ?? '');

    return row.join(FIELD_SEPARATOR);
};

// ═══════════════════════════════════════════════════════════════════════════
// Oeffentliche Schnittstelle
// ═══════════════════════════════════════════════════════════════════════════

export interface DatevExportResult {
    fileName: string;
    bytes: Uint8Array;
}

/**
 * Wie viele Buchungszeilen ein Beleg erzeugt — fuer die Vorschau im
 * Export-Dialog.
 *
 * Sie steht hier und nicht im Dialog, weil die Zeilenzahl seit der Aufteilung
 * in Monatszeilen an der Zuordnung der Positionen haengt. Die Verteilregel ein
 * zweites Mal aufzuschreiben hiesse, zwei Fassungen derselben Regel zu pflegen
 * — und die Vorschau waere dann still falsch statt sichtbar unscharf.
 * Gerechnet wird deshalb mit genau den Funktionen, die auch buchen.
 *
 * `exact` ist falsch, sobald der Beleg Abzuege traegt: ob ein Abzug aus einer
 * Abschlagsrechnung stammt, haengt an der Belegart des abgezogenen Belegs, und
 * die steht erst nach einer Datenbankabfrage fest. Dann ist die Zahl eine
 * OBERGRENZE: je Abzug hoechstens eine zusaetzliche Verrechnungszeile, und je
 * aufgeteiltem Steuersatz zaehlt JEDER Monat der Zuordnung — nicht nur die
 * Monate, deren Anteil am vollen Brutto eine Zeile bekaeme. Ein
 * Vorrechnungsabzug aendert naemlich das Verteilziel (gebucht wird der
 * Zuwachs, nicht das Brutto), und ein Monat, dessen Anteil sich am Brutto auf
 * 0,00 rundet, kann am Zuwachs eine Zeile bekommen. Erst die Zaehlung ueber
 * alle Monate macht die Zahl zu einer harten Obergrenze.
 */
export const bookingRowCount = (invoice: Invoice): { rows: number; exact: boolean } => {
    const deductions = invoice.advanceDeductions?.length ?? 0;
    let rows = 0;
    for (const group of invoice.totals.taxGroups) {
        const gross = grossOfTaxGroup(group);
        if (gross === 0) continue;
        const { netCents } = legNetAndTax(gross, group.taxRateBp, group);
        const months = performanceMonthsForRate(invoice, group.taxRateBp);
        if (months && months.length > 1) {
            const shares = monthlyShares(gross, netCents, months);
            rows += shares ? (deductions > 0 ? months.length : shares.length) : 1;
        } else {
            rows += 1;
        }
    }
    return { rows: rows + deductions, exact: deductions === 0 };
};

/**
 * Baut den EXTF-Buchungsstapel als CP1252-Bytes.
 *
 * Je Steuergruppe und Leistungsmonat eine Erloeszeile und, wenn ein Abschlag
 * abgezogen wird, eine Verrechnungszeile gegen das Anzahlungskonto — siehe
 * bookingLegsForInvoice. Ein Beleg ohne Zuordnung erzeugt unveraendert genau
 * eine Zeile je Steuergruppe. Belege vor ISSUED fuehren zum Abbruch, nicht zum
 * Ueberspringen.
 *
 * Ohne options.belegLinkIds bleibt die Spalte "Beleglink" leer — der Stapel
 * steht dann fuer sich, ohne Verweis auf ein Belegarchiv. Siehe dort.
 */
export const buildExtfBuchungsstapel = (
    invoices: Invoice[],
    config: InvoiceDatevConfig | undefined,
    period: DatevExportPeriod,
    options: DatevExportOptions = {},
): DatevExportResult => {
    if (!config) {
        throw new Error(
            'Keine DATEV-Konfiguration hinterlegt. Beraternummer, Mandantennummer und ' +
                'Erlöskonten sind für den Export zwingend.',
        );
    }

    const blocked = invoices.filter((invoice) => PRE_ISSUE_STATUSES.includes(invoice.status));
    if (blocked.length > 0) {
        const names = blocked
            .map((invoice) => invoice.invoiceNumber ?? `Entwurf ${invoice.id}`)
            .join(', ');
        throw new Error(
            `Nicht ausgestellte Belege können nicht nach DATEV exportiert werden: ${names}. ` +
                'Bitte zuerst ausstellen oder aus der Auswahl entfernen.',
        );
    }

    const missingNumber = invoices.find((invoice) => !invoice.invoiceNumber);
    if (missingNumber) {
        throw new Error(
            `Beleg ${missingNumber.id} hat keine Rechnungsnummer und kann nicht ` +
                'gebucht werden.',
        );
    }

    const rows = invoices.flatMap((invoice) =>
        bookingLegsForInvoice(invoice, config, options.deductionSources).map((leg) =>
            buildBookingRow(invoice, leg, config, options.belegLinkIds),
        ),
    );

    const lines = [
        buildHeaderRow(config, period, options),
        BUCHUNGSSTAPEL_COLUMNS_V13.map(quoteField).join(FIELD_SEPARATOR),
        ...rows,
    ];

    // Abschliessendes CRLF: die letzte Buchungszeile wird wie jede andere
    // beendet, sonst faellt sie bei manchen Importern unter den Tisch.
    const content = `${lines.join(LINE_BREAK)}${LINE_BREAK}`;

    const fileName =
        `EXTF_Buchungsstapel_${formatYearMonthDay(period.from, 'Zeitraum von')}_` +
        `${formatYearMonthDay(period.to, 'Zeitraum bis')}.csv`;

    return { fileName, bytes: toCp1252Bytes(content) };
};

export interface BelegZipEntry {
    /** Ablagepfad des archivierten PDF (Supabase Storage). */
    sourcePath: string;
    /** Zieldateiname im Archiv, nach Rechnungsnummer. */
    fileName: string;
}

/**
 * Liste der Beleg-PDFs fuer das Belegbild-Archiv.
 *
 * Nur die Liste — gezippt wird spaeter in der Oberflaeche mit jszip. Der
 * Grund fuer die Trennung: jszip ist eine reine Browser-Abhaengigkeit, und
 * diese Datei soll ohne sie testbar bleiben.
 *
 * Belege ohne archiviertes PDF werden ausgelassen und in missingPdf gemeldet,
 * damit die Oberflaeche darauf hinweisen kann. Ein stilles Weglassen waere
 * hier besonders tueckisch, weil im Zip niemand ein fehlendes PDF vermisst.
 */
export const buildBelegZipEntries = (
    invoices: Invoice[],
): { entries: BelegZipEntry[]; missingPdf: string[] } => {
    const exportable = invoices.filter(
        (invoice) => !PRE_ISSUE_STATUSES.includes(invoice.status),
    );

    const entries: BelegZipEntry[] = [];
    const missingPdf: string[] = [];

    for (const invoice of exportable) {
        const number = invoice.invoiceNumber;
        if (!invoice.pdfUrl || !number) {
            missingPdf.push(number ?? invoice.id);
            continue;
        }
        entries.push({
            sourcePath: invoice.pdfUrl,
            fileName: `${sanitizeDocumentNumber(number).replace(/\s+/g, '_')}.pdf`,
        });
    }

    return { entries, missingPdf };
};

/**
 * Die offenen Punkte dieses Exporters, in Klartext fuer die Oberflaeche.
 *
 * Gedacht als Quelle fuer einen Warnhinweis im Export-Dialog, solange der
 * Steuerberater keinen Probeimport bestaetigt hat. Wird ein Punkt geklaert,
 * gehoert er hier UND am jeweiligen PRUEFEN-Kommentar entfernt.
 */
export const UNVERIFIED_DATEV_FIELDS: string[] = [
    'Festschreibung in der Kopfzeile (Feld 21): die Referenz schreibt "0", wir schreiben "1". Das ist kein Formatfehler, sondern eine fachliche Entscheidung — ein nicht festgeschriebener Stapel ist in der Kanzlei nachträglich änderbar und damit nicht GoBD-konform. Mit der Kanzlei abstimmen, ob sie den Stapel festgeschrieben annehmen will.',
    'Schreibweise des SKR in der Kopfzeile ("03"/"04") ist ungeprüft; die Referenz lässt Feld 27 leer.',
    'Der BU-Schlüssel bleibt leer, weil der Steuersatz über das Erlöskonto gesteuert wird. Das setzt Automatikkonten in der Kanzlei voraus.',
    'Debitorenkonto: ob debtorAccountBase ein Präfix oder ein Startwert ist und ob DATEV zwingend Sachkontenlänge + 1 Stellen erwartet, ist offen. Die Referenz führt Kundennummern mit Bindestrich (26-141) und bucht auf ein davon getrenntes numerisches Konto (10021); unser Export leitet das Konto aus der Kundennummer ab und wirft, sobald sie nicht rein numerisch ist.',
    'Zulässiger Zeichenvorrat in Belegfeld 1 ist ungeprüft; es wird konservativ auf Buchstaben, Ziffern und wenige Trennzeichen gefiltert.',
    'Maximallängen der Textfelder (Belegfeld 1: 36, Buchungstext: 60, Bezeichnung: 30, Beleginfo-Inhalt: 60) sind ungeprüft. Bei „Beleginfo-Inhalt“ ist die echte DATEV-Grenze hier nicht belegbar — die Referenz schöpft das Feld nicht aus (längster Inhalt: ein Kundenname mit 46 Zeichen); gewählt ist deshalb konservativ dieselbe Grenze wie beim Buchungstext. Ein längerer Firmenname wird gekürzt; vollständig steht er im Debitorenstamm.',
    'Buchungsrichtung der Gutschrift im umsatzsteuerlichen Sinn (SELF_BILLING_CREDIT_NOTE): der Export bucht sie ins SOLL, weil sie eine Abrechnung des Kunden über UNSERE Leistung ist (§14 Abs. 2 Satz 2 UStG) und ihre Beträge deshalb positiv geführt werden. Mit der Kanzlei bestätigen.',
    'Welche Felder in Anführungszeichen stehen müssen und welche nicht, ist ungeprüft; hier sind Textfelder gequotet, Zahlen und Datumsangaben nicht. Die Referenz schreibt Zeile 2 (die Spaltenköpfe) ungequotet, wir gequotet — das hat sich als unkritisch erwiesen, weil DATEV nach Position liest.',
    'Der Betrieb rechnet TEILLEISTUNGEN ab, keine Abschläge: eine wirtschaftlich abgrenzbare, gesondert abgenommene und damit erbrachte Leistung (§ 13 Abs. 1 Nr. 1a UStG) ist echter Erlös, die Umsatzsteuer entsteht mit ihr. Eine Anzahlung ist dagegen noch keine Leistung, sondern nur Geld, und damit eine Verbindlichkeit. Der gesamte Anzahlungspfad — die Konten der erhaltenen Anzahlungen und die Verrechnungszeile — ist deshalb Vorsorge für einen Fall, der hier heute nicht eintritt; die Konten bleiben absichtlich leer, und leer sind sie folgenlos. Offen ist nur, falls doch einmal echte Abschlagsrechnungen gestellt werden: ob die Kanzlei die Auflösung im SELBEN Stapel neben der Erlöszeile will oder getrennt. Die Konten selbst sind geklärt — je Steuersatz eines, SKR04 3272 für 19 % und 3271 für 7 %, weil ein Sammelkonto bei Automatikkonten für 7 % dieselbe Umsatzsteuer zöge wie für 19 %.',
    'Abschläge, die VOR der Einrichtung des Anzahlungskontos exportiert wurden, hat dieser Export auf das Erlöskonto gebucht; ihr Abzug mindert deshalb die Erlöszeile, statt das Anzahlungskonto aufzulösen (Vergleich exported_at gegen den beim Eintragen des Kontos vermerkten Umstellungszeitpunkt). Das beschreibt, wie DIESER Export sie übergeben hat — hat die Kanzlei solche Altbestände von Hand aufs Anzahlungskonto umgebucht, muss sie deren Auflösung dort ebenfalls von Hand nachziehen. Mit der Kanzlei abstimmen, bevor das Konto eingetragen wird.',
    'Externe Alt-Abschläge (public.external_advances) tragen keine Belegart — die Information existiert im System nicht; wie das Vorsystem sie gebucht hat, entscheidet die Einstellung „Alt-Abschläge im Vorsystem gebucht als“. Für diesen Betrieb ist die Frage beantwortet: die Kanzlei hat sie auf SKR04 4400 gebucht, also auf ein ERLÖSKONTO — bereits als Erlös gebuchte Vorrechnung, „PRIOR_INVOICE“. Das passt zur Abrechnung in Teilleistungen, die Erlös sind und keine Anzahlung. Der Wert steht trotzdem bewusst nur in der Konfiguration und ist NICHT im Code vorbelegt: ein anderer Mandant mit echten Abschlagsrechnungen bekäme sonst still die falsche Buchung. Die Antwort gilt für den gesamten Altbestand und darf danach nicht mehr wechseln, sonst treffen zwei Buchungslogiken auf dieselben Altbelege.',
    'Die Referenz füllt "EU-Steuersatz (Bestimmung)" (Spalte 41) und "EU-Mitgliedstaat u. UStID (Ursprung)" (Spalte 123) auch bei reinen Inlandsumsätzen. Das ist als sevDesk-Eigenart eingestuft und wird bewusst NICHT nachgebaut — mit der Kanzlei gegenprüfen.',
];
